/**
 * Das Kaufmodul: Vorschau, Prüfung, Absenden.
 *
 * Eine Datei, weil alles daran an einem Zustand hängt: was in der Finisherzeile
 * steht, was der Knopf darf und was am Ende als Line Item Property
 * mitgeht, ist dieselbe Rechnung, nur dreimal ausgegeben.
 *
 * Was sie tut:
 *
 * 1. **Live-Vorschau.** Drei Blöcke im DOM, ohne Ladezustand: links
 *    die Distanz über KM, in der Mitte Ort, Datum, Linie und Name,
 *    rechts die Zielzeit über TIME (Layout der Druckdatei, Gründer-Sync
 *    02.09.2026). Fehlt die Zielzeit oder ist "keine Zeit" angekreuzt,
 *    schaltet die Vorlage auf ohne-zeit: das Datum rückt nach rechts
 *    über DATUM, die Mitte zeigt Ort, Linie, Name. Fällt eine Angabe
 *    weg, fällt ihr Block mit: das Modul zeigt die Produktregel aus E7,
 *    statt sie zu behaupten (Spec 6.1).
 * 2. **Breitenprüfung.** Die Länge des Namens wird in gerenderter
 *    Breite gemessen, nicht in Zeichen. WILHELMINE ist deutlich breiter
 *    als iiiiiiiiii (Spec 5.1). Der Grenzwert ist die Breite des
 *    mittleren Blocks und bis zur Eichung am Muster geschätzt.
 * 3. **Das Bild.** Dasselbe Satzbild noch einmal im canvas, 1120 x 200,
 *    dunkel auf Creme. Es geht als properties[_vorschau] mit: was der
 *    Kunde bestätigt hat, ist genau das, was gedruckt wird (Spec 6.4).
 *    Ein Wächter prüft vor dem Export die Eckpixel: ein durchsichtiges
 *    Bild geht nie als JPEG (das macht daraus Schwarz), ein schwarzes
 *    geht gar nicht in den Warenkorb (Conversion-Audit 08.10.2026, W1).
 * 4. **Die Knopfsperre.** Ohne Pflichthäkchen und ohne vollständige
 *    Angaben bleibt der Knopf inaktiv, mit aria-disabled und einem
 *    Hinweis, der sagt, was zu tun ist, nicht was fehlt (Spec 6.3).
 * 5. **Vorbelegung aus der Adresse.** name, renntag, distanz, zeit,
 *    rennen, quelle, referenz, nachreichen und key. Ist key gesetzt (der
 *    Schlüssel der Position im Warenkorb, nicht ihre Nummer), ändert das
 *    Absenden genau diese Position, statt eine zweite anzulegen (Spec
 *    12.1, Warenkorb 3). Eine eigene Spur (gpx:, strava:) ist für die
 *    Adresse zu lang: sie kommt über /cart.js aus der Position selbst.
 * 6. **Druckbare Zeichen.** Name, Ortszeile und freie Distanz nehmen nur
 *    Zeichen an, die die Druckschrift hat (assets/ff-druckzeichen.js,
 *    erzeugt aus den Schriften im Repo streckenkarte).
 * 7. **Einmal absenden.** Nach dem ersten gültigen Absenden ist der Knopf
 *    gesperrt, bis die Antwort da ist. Ein Fehler gibt ihn wieder frei,
 *    ein Fehler im Skript selbst hält das Formular an, statt es leer
 *    durchzulassen (Code-Review 25.09.2026, M1 und M4).
 * 8. **Vorbelegung aus dem Rennen.** Die Wahl eines Rennens aus der Liste
 *    setzt dessen Distanz und Renntag, solange der Kunde sie nicht selbst
 *    gewählt hat (25.09.2026, Abschnitt "Vorbelegung aus dem Rennen").
 * 9. **Eigenes Foto statt Streckenkarte** (Vertrag v1, 16.10.2026,
 *    docs/ABWEICHUNGEN.md Punkt 21). Wahl, Zuschnitt und Hochladen stehen
 *    in assets/ff-foto.js (window.ffFoto). Hier hängt das Foto an
 *    denselben Stellen wie jede Angabe: die Rahmenzeichnung zeigt den
 *    Ausschnitt im Kartenfenster, der Knopf bleibt zu, bis Foto, Güte und
 *    Bildrechte stimmen, und beim Absenden geht erst das Foto hoch, dann
 *    die Position in den Warenkorb. Scheitert das Hochladen, geht nichts
 *    in den Warenkorb.
 *
 * Was sie ausdrücklich nicht tut:
 *
 * - **Kein Fremdabruf, keine Bibliothek.** Nur Adressen dieses Shops und,
 *   für das eigene Foto (ff-foto.js), der Endpunkt /api/foto auf der
 *   Hauptdomain aus settings.hauptdomain.
 * - **Kein Farbwert und keine Schrift im Code.** Farben, Schriftfamilie
 *   und Schriftgewicht des Bildes werden per getComputedStyle aus der
 *   gerenderten Finisherzeile gelesen, damit Zeichnung und Bild nie
 *   auseinanderlaufen und Regel 1 gilt: Farben stehen in tokens.css,
 *   die Zeichnungstöne --ff-z-finisherzeile-* in ff-shop.css.
 * - **Keine serverseitige Prüfung.** Die gibt es im Theme-Shop ohne
 *   eigenen Dienst nicht. Deshalb zusätzlich das Prüfblatt vor jedem
 *   Produktionsbatch. Das ist eine bewusste Lücke, keine übersehene
 *   (Spec 5.6).
 *
 * Die Daumenleiste macht ff-reveals.js. Sie hängt am Element mit
 * data-ff-kaufknopf, das der Warenkorbknopf hier trägt.
 */
(function () {
  'use strict';

  /* Ein Kaufmodul je Seite. Der Wächter davor kostet nichts und
     verhindert, dass zwei eingebundene Module doppelt rechnen. */
  if (window.ffVorschauLaeuft) return;
  window.ffVorschauLaeuft = true;

  /* ---------- Maße des Bildes ----------
     Die Finisherzeile ist 280 x 50 mm. Seit dem 22.09.2026 steht sie
     im Bild genau wie in der Druckdatei (Repo streckenkarte, poster.py,
     draw_plaettchen), deshalb sind alle Maße hier Millimeter und
     dieselben Zahlen wie in snippets/finisherzeile-svg.liquid und in den
     Klassen ff-sv-finisherzeile-* (ff-shop.css, Abschnitt 17). Wer hier
     etwas ändert, ändert dort mit, und zuerst im Generator.

     Vier Pixel je Millimeter ergeben 1120 x 200: die Labels sind im
     Druck 3,35 mm hoch (9,5 pt seit ENT-071 vom 22.09.2026), bei zwei
     Pixeln je Millimeter wären sie im Bild kaum zu lesen. Warenkorb und
     Danke-Seite zeigen das Bild weiter im Verhältnis 280 zu 50. */
  var PX = 4;
  var BREITE = 280 * PX;
  var HOEHE = 50 * PX;

  /* Spaltenmitten, vom linken Rand der Zeile. */
  var X_LINKS = 40;
  var X_MITTE = 140;
  var X_RECHTS = 240;

  /* Grade in Millimetern: Punkt mal 0,3528. Laufweiten ebenso.
     Seit ENT-071 (22.09.2026) Werte 20 statt 17 pt, Labels 9,5 statt
     7,5 pt, die Laufweiten sind gleich geblieben. */
  var GRAD = { wert: 7.06, label: 3.35, ort: 9.17, datum: 3.35, name: 7.06 };
  var LAUF = { wert: 0.28, label: 0.56, ort: 0.56, datum: 0.18, name: 0.42 };

  /* Grundlinien, von der Oberkante der Zeile. */
  var ZEILEN = {
    'mit-zeit': { ort: 18.5, datum: 24.3, linie: 27.7, name: 38 },
    'ohne-zeit': { ort: 21.5, linie: 27.7, name: 38 }
  };
  var SEITE_WERT = 23.5;
  var SEITE_LABEL = 31;
  var LINIE_HALB = 52;

  /* Die freie Mitte zwischen Distanz und Zielzeit: breiter, und Ort
     oder Name liefen im Druck in die Seitenblöcke. Der Generator
     begrenzt nichts, die Grenze sitzt hier. Gerechnet, nicht am Muster
     geeicht (Spec 5.1). Seitenwerte dürfen 60 mm breit werden. */
  var MITTE_MM = 150;
  var SEITE_MM = 60;

  /* Obergrenze der Bilddaten. 16000 Zeichen Base64 sind rund 12 KB. */
  var GRENZE = 16000;

  /* Der Renntag liegt nie vor 1970 und hoechstens ein Jahr nach heute,
     gerechnet in Berliner Zeit: um 0:30 Uhr in Berlin ist in London noch
     gestern. Ein kommendes Rennen ist ausdruecklich erlaubt: wer vor dem
     Lauf bestellt, auf der Expo oder als Geschenk, traegt den Renntag im
     Voraus ein (Korrektur 25.09.2026, vorher war jeder Tag nach heute
     gesperrt und damit jede Bestellung vor dem Berlin-Marathon). */
  var RENNTAG_AB = '1970-01-01';
  var RENNTAG_VORLAUF_TAGE = 366;

  /* Ersatz für assets/ff-druckregeln.js, falls die Datei nicht lädt:
     Latin-1 und Latin Extended-A, so weit die Druckschrift sie hat, dazu
     das große ẞ, zu dem der Druck ß versalisiert.
     Lieber einen seltenen Namen abweisen als ein leeres Kästchen
     drucken. Die Breiten rechnen dann jedes Zeichen so breit wie das
     breiteste der Schrift (2274 von 2000 Einheiten, U+25AC). */
  var ERSATZ_MENGE = [[32, 126], [161, 172], [174, 305], [308, 328], [330, 382], [7838, 7838]];
  var ERSATZ_BREITEN = { einheiten: 2000, standard: 2274, breit: [] };

  /* ---------- Die Druckregeln des Generators (29.09.2026) ----------
     Die Zahlen oben sind der Ersatz. Liegen assets/ff-druckregeln.js und
     assets/ff-zeilenregeln.js vor, gelten die Werte, die der Generator
     selbst geschrieben hat (Repo streckenkarte, druckregeln.json): Lage,
     Satz, Grenzen, druckbare Zeichen, Breiten auf den Hundertstelmillimeter
     wie im Druck. pruefung/druckregeln-abgleich.mjs hält den Ersatz auf
     demselben Stand, damit auch er nichts annimmt, was der Druck anders
     setzt. */
  var REGELN = window.ffZeilenregeln && window.ffZeilenregeln.bereit() ? window.ffZeilenregeln : null;
  (function () {
    var l = REGELN && REGELN.lage();
    if (!l) return;
    X_LINKS = l.spalten.links;
    X_MITTE = l.spalten.mitte;
    X_RECHTS = l.spalten.rechts;
    GRAD = { wert: l.grad.wert, label: l.grad.label, ort: l.grad.ort, datum: l.grad.datum, name: l.grad.name };
    LAUF = { wert: l.lauf.wert, label: l.lauf.label, ort: l.lauf.ort, datum: l.lauf.datum, name: l.lauf.name };
    var mit = l.vorlagen['mit-zeit'];
    var ohne = l.vorlagen['ohne-zeit'];
    ZEILEN = {
      'mit-zeit': { ort: mit.ort, datum: mit.datum, linie: l.linie.y_mm, name: mit.name },
      'ohne-zeit': { ort: ohne.ort, linie: l.linie.y_mm, name: ohne.name }
    };
    SEITE_WERT = mit.wert;
    SEITE_LABEL = mit.label;
    LINIE_HALB = l.linie.halb_mm;
    MITTE_MM = l.grenzen.mitte;
    SEITE_MM = l.grenzen.seite;
  })();

  /* ---------- Triathlon, Zeile H2 (Marvin, 26.10.2026) ----------
     Ein Rennen aus der Liste mit der Distanz Triathlon druckt statt der
     Laufzeile die Zeile H2, im selben Fenster 280 x 50 mm und auf
     demselben Passepartout (Marvin, 26.10.2026): oben Name (mittig über
     Spalte 1), groß der Ort, das Datum (mittig über Spalte 4), darunter
     vier Spalten mit Farbbalken, Beschriftung und Zeit:
     SWIM, BIKE, RUN mit den Teilzeiten, FINISH mit der Gesamtzeit (Repo
     streckenkarte, poster.draw_zeile_h2). Die Zahlen unten sind der
     Ersatz, es gelten die aus druckregeln h2. pruefung/druckregeln-
     abgleich.mjs hält beide auf demselben Stand. Lage in mm ab
     Oberkante und linker Kante des Fensters, wie die Laufzeile. */
  /* Seit dem 26.10.2026 alle Triathlon-Distanzen: die Distanz heißt
     "Triathlon <Klasse>" (h2.distanz_muster), die km je Disziplin sind
     mit der Klasse vorbelegt (h2.klassen) und gehen als Km Swim, Km Bike,
     Km Run mit (h2.km_eigenschaften). Ein Rennen aus der Liste trägt nur
     "Triathlon" am Metaobjekt, ohne Klasse. */
  var TRIATHLON = 'Triathlon';
  var H2_DISTANZ = /^Triathlon (Sprintdistanz|Kurzdistanz|Mitteldistanz|Langdistanz)$/;
  var H2_KLASSEN = {
    Sprintdistanz: ['0,75', '20', '5'],
    Kurzdistanz: ['1,5', '40', '10'],
    Mitteldistanz: ['1,9', '90', '21,1'],
    Langdistanz: ['3,8', '180', '42,2']
  };
  var H2_KM_EIGENSCHAFTEN = { swim: 'Km Swim', bike: 'Km Bike', run: 'Km Run' };
  var H2 = {
    fenster_x: 60, fenster_y: 500, breite: 280, hoehe: 50, name_x: 35, ort_x: 140, datum_x: 245,
    kopf: 20, label: 35.2, zeit: 44, balken_breite: 47.6, balken_oben: 25.9, balken_hoehe: 2,
    name_mm: 64, ort_mm: 100, spalte_mm: 62
  };
  var H2_SPALTEN = [35, 105, 175, 245];
  var H2_GRAD = { kopf: 8.467, ort: 15.169, label: 3.881, zeit: 5.997 };
  var H2_LAUF = { kopf: 0.176, ort: 0.282, label: 0.459, zeit: 0.247 };
  /* Stufen in pt (Marvin, 27.10.2026, h2.stufen_pt): ein zu langer Name
     oder Ort steht in der ersten Stufe, mit der er in seine Grenze passt.
     Die erste Stufe ist der Grad aus H2_GRAD, die Laufweite bleibt. */
  var H2_STUFEN = { name: [24, 21, 18], ort: [43, 38, 34, 30] };
  /* Die Disziplinen in Rennfolge und die Eigenschaft, unter der ihre
     Zeit an die Bestellung geht (tagesstapel.TEILZEITEN, h2.eigenschaften). */
  var H2_EIGENSCHAFTEN = { swim: 'Zeit Swim', bike: 'Zeit Bike', run: 'Zeit Run' };
  var H2_ARTEN = ['swim', 'bike', 'run'];
  /* Die Farben der Balken gibt es nur aus der Datei: sie stehen in
     keinem Token. Ohne Datei bleiben die Balken in der Druckfarbe. */
  var H2_FARBEN = null;

  /* Rennen aus der Liste mit eigenen Teilstrecken. Das Metaobjekt rennen
     trägt weder Klasse noch Beschriftung, deshalb stehen sie vorerst hier,
     nach der Referenz des Eintrags (rennen:<handle>). Die Werte stammen
     aus dem Generator, Repo streckenkarte, events/langdistanz-roth.yaml:
     klasse, teile[].nominal_km (als km mit Komma, eine Stelle) und
     teile[].beschriftung, FINISH aus cli.gesamt_label (gerundete Summe der
     Teile). Ändert sich dort etwas oder kommt ein Triathlon in die Liste,
     muss er hier mitgehen. Mit Eintrag: Klasse vorbelegt, km fest und
     gesperrt, Beschriftung aus spalten. Ohne Eintrag gilt Langdistanz,
     und die Beschriftung kommt aus den km-Feldern. */
  var H2_BESCHRIFTUNG = {
    'rennen:langdistanz-roth-2026': {
      klasse: 'Langdistanz',
      km: ['3,8', '180', '42,2'],
      spalten: ['SWIM · 3,8 KM', 'BIKE · 180 KM', 'RUN · 42,2 KM', 'FINISH · 226 KM']
    }
  };
  var H2_BESCHRIFTUNG_ERSATZ = ['SWIM', 'BIKE', 'RUN', 'FINISH'];

  (function () {
    var b = REGELN && REGELN.h2 && REGELN.h2();
    if (!b) return;
    H2 = {
      fenster_x: b.poster_mm.fenster_x, fenster_y: b.poster_mm.fenster_y,
      breite: b.fenster_mm.breite, hoehe: b.fenster_mm.hoehe,
      name_x: b.name_x, ort_x: b.ort_x, datum_x: b.datum_x,
      kopf: b.y_mm.kopf, label: b.y_mm.label, zeit: b.y_mm.zeit,
      balken_breite: b.balken_mm.breite, balken_oben: b.balken_mm.oben, balken_hoehe: b.balken_mm.hoehe,
      name_mm: b.grenzen_mm.name, ort_mm: b.grenzen_mm.ort, spalte_mm: b.grenzen_mm.spalte
    };
    H2_SPALTEN = b.spalten_mm.slice();
    for (var rolle in H2_GRAD) {
      if (!Object.prototype.hasOwnProperty.call(H2_GRAD, rolle) || !b.satz[rolle]) continue;
      H2_GRAD[rolle] = b.satz[rolle].grad_mm;
      H2_LAUF[rolle] = b.satz[rolle].laufweite_mm;
    }
    if (b.eigenschaften) H2_EIGENSCHAFTEN = b.eigenschaften;
    if (b.km_eigenschaften) H2_KM_EIGENSCHAFTEN = b.km_eigenschaften;
    if (b.klassen) H2_KLASSEN = b.klassen;
    if (b.farben && b.farben.length === 4) H2_FARBEN = b.farben.slice();
    if (b.stufen_pt && b.stufen_pt.name && b.stufen_pt.ort) {
      H2_STUFEN = { name: b.stufen_pt.name.slice(), ort: b.stufen_pt.ort.slice() };
    }
  })();

  var TRENNER = ', ';

  function auswahl(wurzel, name) {
    return wurzel.querySelector('[' + name + ']');
  }

  function wert(feld) {
    if (!feld) return '';
    return String(feld.value || '').trim();
  }

  function zweistellig(zahl) {
    return zahl < 10 ? '0' + zahl : String(zahl);
  }

  /* ---------- Druckbare Zeichen (M2) ----------
     Die Mengen stehen in assets/ff-druckregeln.js, geschrieben vom
     Generator aus der cmap der Druckschriften. Geprüft wird die Form,
     die gedruckt wird: Name und Ortszeile in Versalien, die Distanz wie
     getippt (streckenkarte/poster.py, draw_plaettchen). Ohne die Datei
     gilt die Ersatzmenge. */

  function inMenge(menge, punkt) {
    var unten = 0;
    var oben = menge.length - 1;
    while (unten <= oben) {
      var mitte = (unten + oben) >> 1;
      if (punkt < menge[mitte][0]) oben = mitte - 1;
      else if (punkt > menge[mitte][1]) unten = mitte + 1;
      else return true;
    }
    return false;
  }

  /* Versalien wie im Druck: ß wird ẞ, nicht SS (A-606). Die Regel steht in
     der Datei des Generators, ohne sie dieselbe Regel fest. */
  function versal(text) {
    if (REGELN) return REGELN.versal(text);
    return String(text || '').split('ß').join('ẞ').toUpperCase();
  }

  /* Das erste Zeichen, wie getippt, das der Druck nicht setzen kann,
     sonst ''. Zeichen für Zeichen, damit die Meldung das getippte
     Zeichen nennt und nicht seine Versalform. */
  function fremdesZeichen(text, feld) {
    if (REGELN) return REGELN.fremdesZeichen(feld, text);
    var menge = ERSATZ_MENGE;
    for (var i = 0; i < text.length; ) {
      var punkt = text.codePointAt(i);
      var zeichen = String.fromCodePoint(punkt);
      var gedruckt = feld === 'distanz' ? zeichen : versal(zeichen);
      for (var k = 0; k < gedruckt.length; ) {
        var teil = gedruckt.codePointAt(k);
        if (!inMenge(menge, teil)) return zeichen;
        k += teil > 0xffff ? 2 : 1;
      }
      i += punkt > 0xffff ? 2 : 1;
    }
    return '';
  }

  /* Unsichtbares (Steuerzeichen, Leerraum, Trennfugen, allein stehende
     Akzente) nennt die Meldung beim Codepunkt, sonst stünde dort ein
     leeres Paar Klammern. */
  function zeichenZeigen(zeichen) {
    var punkt = zeichen.codePointAt(0);
    var unsichtbar =
      punkt < 0x20 ||
      (punkt >= 0x7f && punkt <= 0x9f) ||
      punkt === 0xad ||
      (punkt >= 0x300 && punkt <= 0x36f) ||
      (punkt >= 0x200b && punkt <= 0x200f) ||
      (punkt >= 0x2028 && punkt <= 0x202f) ||
      (punkt >= 0x2060 && punkt <= 0x206f) ||
      punkt === 0xfeff ||
      /\s/.test(zeichen);
    if (!unsichtbar) return zeichen;
    var hex = punkt.toString(16).toUpperCase();
    while (hex.length < 4) hex = '0' + hex;
    return 'U+' + hex;
  }

  /* Heute in Berlin als JJJJ-MM-TT. Ohne Zeitzonen-Unterstützung im
     Browser gilt das Datum des Geräts. */
  /* Spaetester erlaubter Renntag: heute in Berlin plus Vorlauf. */
  function renntagBis() {
    var t = heuteBerlin().split('-');
    var d = new Date(Date.UTC(+t[0], +t[1] - 1, +t[2]) + RENNTAG_VORLAUF_TAGE * 86400000);
    return d.toISOString().slice(0, 10);
  }

  function heuteBerlin() {
    var jetzt = new Date();
    try {
      var teile = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(jetzt);
      var t = {};
      for (var i = 0; i < teile.length; i++) t[teile[i].type] = teile[i].value;
      if (t.year && t.month && t.day) return t.year + '-' + t.month + '-' + t.day;
    } catch (fehler) {
      /* fällt auf das Gerätedatum zurück */
    }
    return jetzt.getFullYear() + '-' + zweistellig(jetzt.getMonth() + 1) + '-' + zweistellig(jetzt.getDate());
  }

  /* JJJJ-MM-TT, und den Tag gibt es wirklich (kein 31.02.). */
  function echtesDatum(roh) {
    if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(roh)) return false;
    var teile = roh.split('-');
    var datum = new Date(Date.UTC(+teile[0], +teile[1] - 1, +teile[2]));
    return datum.getUTCFullYear() === +teile[0] && datum.getUTCMonth() === +teile[1] - 1 && datum.getUTCDate() === +teile[2];
  }

  function start() {
    var wurzel = document.querySelector('[data-ff-kaufmodul]');
    if (!wurzel) return;

    var form = wurzel.querySelector('form');
    if (!form) return;

    var eName = auswahl(wurzel, 'data-ff-name');
    var eRenntag = auswahl(wurzel, 'data-ff-renntag');
    var eDistanz = auswahl(wurzel, 'data-ff-distanz');
    var eDistanzFrei = auswahl(wurzel, 'data-ff-distanz-frei');
    var eDistanzFreiFeld = auswahl(wurzel, 'data-ff-distanz-frei-feld');
    var eDistanzTrail = auswahl(wurzel, 'data-ff-distanz-trail');
    var eTrailKm = auswahl(wurzel, 'data-ff-distanz-trail-km');
    var eTrailHm = auswahl(wurzel, 'data-ff-distanz-trail-hm');
    var eDistanzUltra = auswahl(wurzel, 'data-ff-distanz-ultra');
    var eUltraZahl = auswahl(wurzel, 'data-ff-distanz-ultra-zahl');
    var eEinheitBlock = auswahl(wurzel, 'data-ff-distanz-einheit-block');
    var eEinheit = auswahl(wurzel, 'data-ff-distanz-einheit');
    var eHoeheName = auswahl(wurzel, 'data-ff-trail-hoehe-name');
    var eZeitH = auswahl(wurzel, 'data-ff-zeit-h');
    var eZeitM = auswahl(wurzel, 'data-ff-zeit-m');
    var eZeitS = auswahl(wurzel, 'data-ff-zeit-s');
    var eKeineZeit = auswahl(wurzel, 'data-ff-keine-zeit');
    var eRennen = auswahl(wurzel, 'data-ff-rennen');
    var eRennenFrei = auswahl(wurzel, 'data-ff-rennen-frei');
    var eRennenFreiFeld = auswahl(wurzel, 'data-ff-rennen-frei-feld');
    var eOhneKarte = auswahl(wurzel, 'data-ff-ohne-karte');
    var eNachreichen = auswahl(wurzel, 'data-ff-nachreichen');
    var eSprachen = wurzel.querySelectorAll('[data-ff-sprache]');
    var eHaken = auswahl(wurzel, 'data-ff-haken');
    var eKnopf = auswahl(wurzel, 'data-ff-knopf');
    var eKnopfhinweis = auswahl(wurzel, 'data-ff-knopfhinweis');
    var eSportwoerter = wurzel.querySelectorAll('[data-ff-sportwort]');

    /* Triathlon (snippets/feld-zeit.liquid): die Teilzeiten je
       Disziplin, Name und Hilfe des Zeitfelds wechseln zu Gesamtzeit. */
    var eTeilzeiten = auswahl(wurzel, 'data-ff-teilzeiten');
    var eZeitName = auswahl(wurzel, 'data-ff-zeit-name');
    var eZeitOptional = auswahl(wurzel, 'data-ff-zeit-optional');
    var eZeitHilfe = auswahl(wurzel, 'data-ff-zeit-hilfe');
    var eKeineZeitZeile = auswahl(wurzel, 'data-ff-keine-zeit-zeile');
    var eTriathlonBlock = auswahl(wurzel, 'data-ff-distanz-triathlon');
    var eTriKmHilfe = auswahl(wurzel, 'data-ff-tri-km-hilfe');
    var triKm = {};
    for (var tk0 = 0; tk0 < H2_ARTEN.length; tk0++) {
      var kmFeld = wurzel.querySelector('[data-ff-tri-km="' + H2_ARTEN[tk0] + '"]');
      var kmVerborgen = wurzel.querySelector('[data-ff-tri-km-prop="' + H2_ARTEN[tk0] + '"]');
      if (!kmFeld || !kmVerborgen) continue;
      var kmSchluessel = H2_KM_EIGENSCHAFTEN[H2_ARTEN[tk0]];
      if (kmSchluessel) {
        kmVerborgen.name = 'properties[' + kmSchluessel + ']';
        kmVerborgen.setAttribute('data-ff-prop', kmSchluessel);
      }
      triKm[H2_ARTEN[tk0]] = {
        feld: kmFeld,
        verborgen: kmVerborgen,
        name: kmFeld.getAttribute('data-ff-tri-km-name') || H2_ARTEN[tk0]
      };
    }
    var teilzeitFelder = {};
    for (var ta = 0; ta < H2_ARTEN.length; ta++) {
      var gruppe = wurzel.querySelector('[data-ff-teilzeit="' + H2_ARTEN[ta] + '"]');
      var verborgen = wurzel.querySelector('[data-ff-teilzeit-prop="' + H2_ARTEN[ta] + '"]');
      if (!gruppe || !verborgen) continue;
      /* Der Schlüssel der Eigenschaft kommt aus den Druckregeln, das
         Markup trägt denselben als Ersatz (Abgleich hält beide gleich). */
      var schluessel = H2_EIGENSCHAFTEN[H2_ARTEN[ta]];
      if (schluessel) {
        verborgen.name = 'properties[' + schluessel + ']';
        verborgen.setAttribute('data-ff-prop', schluessel);
      }
      teilzeitFelder[H2_ARTEN[ta]] = {
        h: gruppe.querySelector('[data-ff-teilzeit-h]'),
        m: gruppe.querySelector('[data-ff-teilzeit-m]'),
        s: gruppe.querySelector('[data-ff-teilzeit-s]'),
        name: gruppe.getAttribute('data-ff-teilzeit-name') || H2_ARTEN[ta],
        verborgen: verborgen
      };
    }

    var eFinisherzeile = auswahl(wurzel, 'data-ff-finisherzeile');
    var eFinisherzeileDistanz = auswahl(wurzel, 'data-ff-finisherzeile-distanz');
    var eFinisherzeileLabel = auswahl(wurzel, 'data-ff-finisherzeile-label');
    var eFinisherzeileOrt = auswahl(wurzel, 'data-ff-finisherzeile-ort');
    var eFinisherzeileDatum = auswahl(wurzel, 'data-ff-finisherzeile-datum');
    var eFinisherzeileLinie = auswahl(wurzel, 'data-ff-finisherzeile-linie');
    var eFinisherzeileName = auswahl(wurzel, 'data-ff-finisherzeile-name');
    var eFinisherzeileRechts = auswahl(wurzel, 'data-ff-finisherzeile-rechts');
    var eFinisherzeileRechtsLabel = auswahl(wurzel, 'data-ff-finisherzeile-rechts-label');
    var eFinisherzeileLeer = auswahl(wurzel, 'data-ff-finisherzeile-leer');
    var eAnsage = auswahl(wurzel, 'data-ff-finisherzeile-ansage');
    var eCanvas = auswahl(wurzel, 'data-ff-canvas');

    var streckenwahl = auswahl(wurzel, 'data-ff-streckenwahl');
    var eKnopftext = auswahl(wurzel, 'data-ff-knopftext');
    var KNOPFTEXT = eKnopftext ? eKnopftext.textContent : '';

    /* Alle Sätze, die dieses Skript dem Kunden zeigt, kommen aus dem
       Liquid (locales/*.json über data-ff-text-* am Kaufmodul und an
       den Bausteinen), wie auf der Hauptdomain. Die deutschen Fallbacks
       hier greifen nur, wenn ein Attribut fehlt. */
    function textAus(element, art, fallback) {
      var wert = element ? element.getAttribute('data-ff-text-' + art) : '';
      return wert || fallback;
    }
    var eModul = wurzel.querySelector('[data-ff-kaufmodul]') || wurzel;

    /* Der Knopf startet inaktiv im Markup: ohne dieses Skript gibt es
       keine Vorschau, also auch keine Bestätigung. Freigegeben wird er
       erst am Ende von start(), wenn der Absende-Handler hängt: bricht
       das Skript vorher ab, bleibt er zu, statt ein leeres Formular
       durchzulassen (M4). Ab dann führt aria-disabled den Zustand, damit
       der Knopf erreichbar bleibt und sein Hinweis vorgelesen wird. */
    if (eKnopf) eKnopf.setAttribute('aria-disabled', 'true');

    var abgeschickt = false;
    var beruehrt = {};
    /* Der Schlüssel der Position, die "Ändern" bearbeitet (item.key),
       sonst null. Nie ihre Nummer: die verschiebt sich, sobald im
       Warenkorb eines anderen Tabs etwas dazukommt oder wegfällt (M3). */
    var position = null;
    /* Zwischen dem ersten gültigen Absenden und der Antwort (M1). */
    var sendet = false;
    /* Solange die Position aus /cart.js nachgeladen wird. */
    var vorbelegungLaeuft = false;
    /* Solange das eigene Foto hochgeht: der Stand fürs Knopfhinweisfeld. */
    var hochladeText = '';
    var letzterStand = { vollstaendig: false, haken: false };

    /* ---------- Eigenschaften ----------
       Ein Zugriff, ein Ort: alle versteckten Felder tragen data-ff-prop
       mit genau dem Schlüssel aus Spec 4.3. */
    function prop(schluessel) {
      return wurzel.querySelector('[data-ff-prop="' + schluessel + '"]');
    }

    function propSetzen(schluessel, inhalt) {
      var feld = prop(schluessel);
      if (feld) feld.value = inhalt || '';
    }

    /* ---------- Fehleranzeige ----------
       Nie nur über Farbe: das Ausrufezeichen steht in der Klasse
       ff-fehler, der Text sagt, was zu tun ist, und aria-describedby
       verbindet beides mit dem Feld. */
    function fehlerSetzen(name, text, feld) {
      var kasten = wurzel.querySelector('[data-ff-fehler="' + name + '"]');
      if (!kasten) return;

      if (text) {
        kasten.textContent = text;
        kasten.setAttribute('data-an', 'ja');
      } else {
        kasten.textContent = '';
        kasten.setAttribute('data-an', 'nein');
      }

      if (feld) feld.setAttribute('aria-invalid', text ? 'true' : 'false');
    }

    /* Beim Triathlon zuerst die Fassung mit -triathlon, wo es eine gibt:
       "Renntag" statt "Lauftag", "Rennen" statt "Lauf" (Prüfstab 2, N1). */
    var sportJetzt = 'standard';

    function fehlertext(name, art) {
      var kasten = wurzel.querySelector('[data-ff-fehler="' + name + '"]');
      if (!kasten) return '';
      if (sportJetzt === 'triathlon') {
        var eigen = kasten.getAttribute('data-ff-text-' + art + '-triathlon');
        if (eigen) return eigen;
      }
      return kasten.getAttribute('data-ff-text-' + art) || '';
    }

    /* ---------- Die Zielzeit ----------
       Drei Zahlenfelder, kein Freitext. Leer ist kein Fehler: die
       Optionalität ist verbindlich (E7). Ist eines der drei gefüllt,
       gelten die anderen als null, damit 45 Minuten nicht zu einem
       Fehler werden, nur weil die Stunde leer blieb. */
    function zielzeit() {
      /* Beim Triathlon ist die Zielzeit die Gesamtzeit und Pflicht:
         "keine Zeit" ist dann ausgeblendet und gilt nicht. */
      if (eKeineZeit && eKeineZeit.checked && !triathlonAn()) return { text: '', fehler: false };
      return zeitAus(eZeitH, eZeitM, eZeitS);
    }

    /* Eine Zeit aus drei Zahlenfeldern, für die Zielzeit und die
       Teilzeiten beim Triathlon. Leer ist hier kein Fehler, ob sie
       Pflicht ist, entscheidet der Aufrufer. */
    function zeitAus(feldH, feldM, feldS) {
      var roh = [wert(feldH), wert(feldM), wert(feldS)];
      if (!roh[0] && !roh[1] && !roh[2]) return { text: '', fehler: false };

      var zahlen = [];
      for (var i = 0; i < 3; i++) {
        var stueck = roh[i] === '' ? '0' : roh[i];
        if (!/^[0-9]{1,2}$/.test(stueck)) return { text: '', fehler: true };
        zahlen.push(parseInt(stueck, 10));
      }

      if (zahlen[0] > 99 || zahlen[1] > 59 || zahlen[2] > 59) return { text: '', fehler: true };
      /* 0:00:00 ist keine Zielzeit, sondern ein Tippfehler (N5). */
      if (zahlen[0] === 0 && zahlen[1] === 0 && zahlen[2] === 0) return { text: '', fehler: true, art: 'null' };

      var text = zahlen[0] + ':' + zweistellig(zahlen[1]) + ':' + zweistellig(zahlen[2]);
      /* Was der Tagesstapel nicht als Zeit liest, geht nicht in den Korb. */
      if (REGELN && !REGELN.zielzeitGueltig(text)) return { text: '', fehler: true };
      return { text: text, fehler: false };
    }

    /* Der Browser liefert JJJJ-MM-TT, auf die Finisherzeile kommt
       TT.MM.JJJJ (Markensprache 7: in Tabellen 27.09.2026). */
    function renntagLesbar() {
      var roh = wert(eRenntag);
      if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(roh)) return '';
      var teile = roh.split('-');
      return teile[2] + '.' + teile[1] + '.' + teile[0];
    }

    /* Leer ist "fehlt", sonst muss der Tag echt sein, darf nicht vor 1970
       liegen und hoechstens ein Jahr nach heute in Berlin (N5). Weiter in
       der Zukunft ist fast immer ein vertipptes Jahr. */
    function renntagFehler() {
      var roh = wert(eRenntag);
      if (!roh || !echtesDatum(roh)) return 'fehlt';
      if (roh < RENNTAG_AB) return 'alt';
      if (roh > renntagBis()) return 'zukunft';
      return '';
    }

    /* Was gedruckt wird: NFC, Leerraum zu einem Leerzeichen, ohne Rand.
       Ein zerlegtes é (e plus Akzent) setzt der Druck sonst als zwei
       Zeichen, ein geschütztes Leerzeichen hat die Druckschrift gar
       nicht. */
    function druckText(feld) {
      if (REGELN) return REGELN.bereinigt(wert(feld));
      var text = wert(feld);
      if (text.normalize) text = text.normalize('NFC');
      return text.replace(/\s+/g, ' ');
    }

    /* ---------- Trail: Strecke und Höhenmeter ----------
       Lenny, 29.09.2026: bei Trail zwei freiwillige Zahlenfelder. Auf die
       Bestellung (properties[Distanz]) geht der Klartext, "30 km · 1.850 Hm",
       "30 km" oder "1.850 Hm", beide leer bleibt es bei "Trail". Auf der
       Finisherzeile steht wie bei jeder Distanz die Zahl über dem Label:
       "30 · 1.850" über "KM · HM". Der Tagesstapel zerlegt den Klartext
       genauso (Repo streckenkarte, tagesstapel.distanz_satz). Wer die Form
       hier ändert, ändert sie dort mit. */
    var MITTELPUNKT = ' · ';

    /* Meilen (Marvin, 30.09.2026): die Strecke bei Trail, Ultra und der
       freien Distanz geht als Zahl mit Einheit an die Bestellung, "20 mi"
       oder "20 km". Auf der Finisherzeile steht die Zahl über MI oder KM.
       Ein Schalter für alle drei, sichtbar nur, wenn eine davon gewählt ist. */
    var EINHEIT_LABEL = { km: 'KM', mi: 'MI' };

    /* Die Höhe folgt dem Schalter (Marvin, 30.09.2026): in Kilometern
       Höhenmeter "1.850 Hm" über HM, in Meilen Fuß "6.070 ft" über FT.
       Bis zum 09.10.2026 hieß die Tabelle HOEHE und verdeckte damit die
       Bildhöhe HOEHE (50 mm mal PX) für alles in start(), auch für
       bildZeichnen(): fillRect(0, 0, BREITE, {km, mi}) zeichnete nichts,
       das Bild blieb durchsichtig, und der JPEG-Ausweichpfad machte aus
       durchsichtig schwarz (Conversion-Audit 08.10.2026, W1). */
    var HM_EINHEIT = { km: 'Hm', mi: 'ft' };
    var HM_EINHEIT_LABEL = { km: 'HM', mi: 'FT' };

    function einheit() {
      return eEinheit && eEinheit.value === 'mi' ? 'mi' : 'km';
    }

    function einheitMoeglich() {
      return !!eDistanz && (eDistanz.value === 'frei' || eDistanz.value === 'Trail' || eDistanz.value === 'Ultra');
    }

    function trailAn() {
      return !!eDistanz && eDistanz.value === 'Trail' && !!eTrailKm && !!eTrailHm;
    }

    function ultraAn() {
      return !!eDistanz && eDistanz.value === 'Ultra' && !!eUltraZahl;
    }

    function tausender(zahl) {
      return String(zahl).replace(/\B(?=([0-9]{3})+(?![0-9]))/g, '.');
    }

    /* Strecke: bis 999, bis drei Nachkommastellen, Komma oder Punkt, in
       der gewählten Einheit. Dieselbe Form für Trail, Ultra und die freie
       Distanz. */
    function strecke(feld) {
      var roh = wert(feld).replace(/\s+/g, '');
      if (!roh) return { text: '', fehler: false };
      var teile = /^([0-9]{1,3})(?:[.,]([0-9]{1,3}))?$/.exec(roh);
      if (!teile) return { text: '', fehler: true };
      var ganz = parseInt(teile[1], 10);
      var rest = (teile[2] || '').replace(/0+$/, '');
      if (!ganz && !rest) return { text: '', fehler: true };
      return { text: ganz + (rest ? ',' + rest : ''), fehler: false };
    }

    function trailKm() {
      return strecke(eTrailKm);
    }

    /* Ultra (Marvin, 30.09.2026): eine freiwillige Strecke. Mit Zahl geht
       "Ultra · 100 km" an die Bestellung, auf der Finisherzeile steht 100
       über KM. Ohne Zahl bleibt es bei "Ultra". */
    function ultraAngaben() {
      var zahl = strecke(eUltraZahl);
      return {
        zahl: zahl,
        bestellung: zahl.text ? 'Ultra' + MITTELPUNKT + zahl.text + ' ' + einheit() : 'Ultra',
        satz: zahl.text ? { zahl: zahl.text, label: EINHEIT_LABEL[einheit()] } : null
      };
    }

    /* Die freie Distanz ist seit dem 30.09.2026 eine Zahl mit Einheit,
       kein Freitext mehr: "16,1 km" oder "10 mi". */
    function freiAngaben() {
      var zahl = strecke(eDistanzFreiFeld);
      return { zahl: zahl, bestellung: zahl.text ? zahl.text + ' ' + einheit() : '' };
    }

    /* Höhenmeter: ganze Zahl bis 99.999, Tausenderpunkt darf mitkommen. */
    function trailHm() {
      var roh = wert(eTrailHm).replace(/\s+/g, '');
      if (!roh) return { text: '', fehler: false };
      if (!/^(?:[0-9]{1,5}|[0-9]{1,2}\.[0-9]{3})$/.test(roh)) return { text: '', fehler: true };
      var zahl = parseInt(roh.replace('.', ''), 10);
      if (!zahl) return { text: '', fehler: true };
      return { text: tausender(zahl), fehler: false };
    }

    function trailAngaben() {
      var km = trailKm();
      var hm = trailHm();
      var bestellung = [];
      var zahl = [];
      var label = [];
      if (km.text) {
        bestellung.push(km.text + ' ' + einheit());
        zahl.push(km.text);
        label.push(EINHEIT_LABEL[einheit()]);
      }
      if (hm.text) {
        bestellung.push(hm.text + ' ' + HM_EINHEIT[einheit()]);
        zahl.push(hm.text);
        label.push(HM_EINHEIT_LABEL[einheit()]);
      }
      return {
        km: km,
        hm: hm,
        bestellung: bestellung.join(MITTELPUNKT),
        zahl: zahl.join(MITTELPUNKT),
        label: label.join(MITTELPUNKT)
      };
    }

    /* Rückweg aus dem Warenkorb: "30 km · 1.850 Hm", "20 mi · 6.070 ft"
       oder "20 mi" in die Felder, "Ultra · 100 km" in das Ultra-Feld. Fuß
       ohne Strecke heißt Meilen. */
    function trailZerlegen(text) {
      var teile = /^(?:([0-9]{1,3}(?:,[0-9]{1,3})?) (km|mi))?(?: · )?(?:([0-9]{1,2}(?:\.[0-9]{3})?|[0-9]{1,3}) (Hm|ft))?$/.exec(text || '');
      if (!teile || (!teile[1] && !teile[3])) return null;
      var einheitDaraus = teile[2] || (teile[4] === 'ft' ? 'mi' : teile[4] === 'Hm' ? 'km' : '');
      return { km: teile[1] || '', einheit: einheitDaraus, hm: teile[3] || '' };
    }

    function ultraZerlegen(text) {
      var teile = /^Ultra · ([0-9]{1,3}(?:,[0-9]{1,3})?) (km|mi)$/.exec(text || '');
      return teile ? { zahl: teile[1], einheit: teile[2] } : null;
    }

    function distanzWert() {
      if (!eDistanz) return '';
      if (eDistanz.value === 'frei') return eDistanzFreiFeld ? freiAngaben().bestellung : '';
      if (trailAn()) return trailAngaben().bestellung || eDistanz.value;
      if (ultraAn()) return ultraAngaben().bestellung;
      return eDistanz.value;
    }

    /* Auf der Finisherzeile steht die Distanz als Kilometerzahl über dem
       Label KM. Die Zahl hängt als data-ff-km an der Option, nicht
       hier: das Skript kennt keine Streckenlängen. Ohne Zahl (etwa
       bei einem Freitext) steht der Name der Distanz. */
    function distanzKm() {
      if (!eDistanz || !eDistanz.value) return '';
      var gewaehlt = eDistanz.options[eDistanz.selectedIndex];
      var km = gewaehlt ? gewaehlt.getAttribute('data-ff-km') || '' : '';
      return km || distanzWert();
    }

    /* Zahl und Label der linken Spalte. Mit den Druckregeln genau wie der
       Tagesstapel aus dem Wert, der als Distanz an die Bestellung geht
       (streckenkarte, tagesstapel.distanz_satz): eine freie Distanz
       "42,2 km" steht dann auch in der Vorschau als 42,2 über KM, wie im
       Druck. Ohne die Datei gilt die Rechnung darunter. */
    function distanzSatz() {
      if (REGELN) return REGELN.distanzSatz(distanzWert(), druckSprache());
      if (trailAn()) {
        var trail = trailAngaben();
        if (trail.zahl) return { zahl: trail.zahl, label: trail.label };
      }
      if (ultraAn() && ultraAngaben().satz) return ultraAngaben().satz;
      if (eDistanz && eDistanz.value === 'frei' && freiAngaben().zahl.text) {
        return { zahl: freiAngaben().zahl.text, label: EINHEIT_LABEL[einheit()] };
      }
      /* Wort ohne Zahl (Marvin, 30.09.2026): in Versalien, ohne Label,
         in der Sprache der Seite wie die Option ("Relay" auf Englisch). */
      var km = distanzKm();
      if (km && !/[0-9]/.test(km)) {
        var option = eDistanz && eDistanz.value === km ? eDistanz.options[eDistanz.selectedIndex] : null;
        var wort = option ? option.textContent.trim() || km : km;
        return { zahl: versal(wort), label: '' };
      }
      return { zahl: km, label: LINKS_LABEL };
    }

    /* LF-057 (Leon, 07.09.2026): "Ohne Streckenkarte" ist eine dritte
       Wahl neben einem Listenrennen und dem Freitext. An der
       Kartenposition steht dann ein gedruckter Platzhalterrahmen mit
       dem Wort Foto, der Kunde legt sein selbst gedrucktes Bild ein.

       Fuer den Ort auf der Finisherzeile zaehlt diese Wahl wie ein
       Freitext: ohne Karte heisst nicht ohne Rennen, und Ort, Datum und
       Distanz stehen weiter auf der Namenszeile. Fuer die Strecke
       dagegen ist sie eine eigene Referenz, damit die Produktion die
       Postervariante ohne Karte erkennt. */
    function ohneKarte() {
      /* Zwei Wege zur selben Wahl: als Eintrag der Rennliste, oder,
         solange die Liste leer ist und es gar kein Auswahlfeld gibt,
         als Ankreuzfeld im Ersatzzustand. */
      if (eOhneKarte && eOhneKarte.checked) return true;
      return !!eRennen && eRennen.value === 'ohne-karte';
    }

    /* Beide Faelle blenden dasselbe Freitextfeld ein und nehmen von dort
       den Ort. Sie unterscheiden sich erst bei der Streckenreferenz. */
    function freitextFall() {
      return !eRennen || eRennen.value === 'frei' || ohneKarte();
    }

    function rennenWert() {
      if (!eRennen) return druckText(eRennenFreiFeld);
      if (freitextFall()) return druckText(eRennenFreiFeld);
      return eRennen.value;
    }

    /* Der Ort in der Mitte der Finisherzeile kommt aus der Streckenwahl:
       data-ort am gewählten Rennen. Fehlt er, steht die Bezeichnung
       des Rennens, beim Freitext der getippte Text. */
    function ortWert() {
      if (freitextFall() || !eRennen.value) return druckText(eRennenFreiFeld);
      var gewaehlt = eRennen.options[eRennen.selectedIndex];
      var ort = gewaehlt ? gewaehlt.getAttribute('data-ort') || '' : '';
      return ort || eRennen.value;
    }

    /* Die Sprache der Finisherzeile wählt der Kunde je Rahmen (Marvin,
       01.10.2026, snippets/feld-sprache.liquid): "Deutsch" oder "English",
       vorausgewählt nach der Seite. Beschriftung rechts (ZEIT oder TIME,
       DATUM oder DATE), Langdatum und Distanzwort folgen ihr, genau wie
       im Druck, der die Eigenschaft vor customerLocale liest. Nur ohne
       Druckregeln gilt der Ersatz aus dem Markup in der Shopsprache. */
    function spracheWahl() {
      for (var i = 0; i < eSprachen.length; i++) {
        if (eSprachen[i].checked) return eSprachen[i].value;
      }
      return '';
    }

    function druckSprache() {
      if (!REGELN) return null;
      var seite = document.documentElement.lang;
      return REGELN.spracheDerPosition(spracheWahl(), seite) || REGELN.sprache(seite);
    }

    function label(name, ersatz) {
      if (REGELN) return REGELN.beschriftung(druckSprache())[name] || ersatz;
      if (!eFinisherzeile) return ersatz;
      return eFinisherzeile.getAttribute('data-ff-label-' + name) || ersatz;
    }

    /* Traegt die Pruefmarkierung: ein selbst geschriebener Rennname ist
       ungeprueft, egal ob mit oder ohne Karte. */
    function rennenAusFreitext() {
      return freitextFall();
    }

    function streckenreferenz() {
      if (ohneKarte()) return 'ohne-karte';
      if (!eRennen || eRennen.value === 'frei' || !eRennen.value) return '';
      var gewaehlt = eRennen.options[eRennen.selectedIndex];
      return gewaehlt ? gewaehlt.getAttribute('data-referenz') || '' : '';
    }

    function nachreichenAn() {
      return !!(eNachreichen && eNachreichen.checked);
    }

    /* ---------- Triathlon (Marvin, 26.10.2026) ----------
       Triathlon heißt: die gewählte Distanz ist eine Triathlon-Klasse
       ("Triathlon Mitteldistanz"). Seit dem Abend des 26.10.2026 hängt es
       an der Distanz, nicht mehr am Rennen: auch "Anderer Lauf" (Freitext,
       Prüfmarkierung wie immer) und "Ohne Streckenkarte" drucken die
       Zeile H2, mit den km aus den Feldern. Eine eigene Spur (gpx:,
       strava:) mit Triathlon legt der Tagesstapel auf die Prüfliste, das
       Kaufmodul hält sie vorher an (triathlonSpur).

       Foto (#87, hier eingemergt, Marvin 26.10.2026: Foto für Lauf und
       Triathlon): ein Motiv Foto ändert an der Klasse nichts, es ersetzt
       nur die Karte im Kartenfenster. Deshalb fragt triathlonKlasse() das
       Motiv bewusst nicht ab. */
    function triathlonKlasse() {
      if (!eDistanz || nachreichenAn()) return '';
      if (REGELN && REGELN.triathlonKlasse && REGELN.h2()) return REGELN.triathlonKlasse(eDistanz.value) || '';
      var m = H2_DISTANZ.exec(eDistanz.value || '');
      return m ? m[1] : '';
    }

    function triathlonAn() {
      return !!triathlonKlasse();
    }

    function eigeneSpur() {
      return !!(wurzel.getAttribute('data-ff-streckenquelle') && wurzel.getAttribute('data-ff-streckenreferenz'));
    }

    function triathlonSpur() {
      return triathlonAn() && eigeneSpur();
    }

    /* Das gewählte Rennen aus der Liste (Option mit rennen:), sonst null. */
    function listenOption() {
      if (!eRennen || freitextFall() || !eRennen.value) return null;
      var gewaehlt = eRennen.options[eRennen.selectedIndex];
      return gewaehlt && (gewaehlt.getAttribute('data-referenz') || '').indexOf('rennen:') === 0 ? gewaehlt : null;
    }

    /* Der Eintrag aus H2_BESCHRIFTUNG zum gewählten Rennen: dessen km und
       Beschriftung gelten, die Felder sind gesperrt. Nicht bei eigener Spur. */
    function rennenTeile() {
      var gewaehlt = listenOption();
      if (!gewaehlt || eigeneSpur()) return null;
      return H2_BESCHRIFTUNG[gewaehlt.getAttribute('data-referenz')] || null;
    }

    /* Rennen aus der Liste und Distanz passen nicht zusammen: ein
       Triathlon-Rennen mit einer Laufdistanz, ein Laufrennen mit einer
       Triathlon-Klasse, oder eine andere Klasse als die des Rennens. Der
       Tagesstapel legte das auf die Prüfliste, das Kaufmodul sagt es vorher. */
    function triathlonRennenKonflikt() {
      var gewaehlt = listenOption();
      if (!gewaehlt || eigeneSpur() || nachreichenAn() || !eDistanz || !eDistanz.value) return false;
      var klasse = triathlonKlasse();
      if ((gewaehlt.getAttribute('data-distanz') === TRIATHLON) !== !!klasse) return true;
      var teile = rennenTeile();
      return !!(klasse && teile && teile.klasse && teile.klasse !== klasse);
    }

    /* ---------- Die km je Disziplin ----------
       Getippt mit Komma oder Punkt, an die Bestellung geht das Komma
       (h2.km_muster). Vorbelegt mit der Klasse.

       Seit dem 27.10.2026 (Prüfstab 2, M2) belegt jeder Wechsel von einer
       Klasse auf eine andere alle drei Felder neu, auch nach einer eigenen
       Eingabe. Bis dahin blieben nach einer einzigen eigenen Zahl alle drei
       km der alten Klasse stehen, und eine Langdistanz ging mit 20 km Rad
       in den Druck. Eigene km einer Sprintdistanz sagen über eine
       Langdistanz nichts. Wurden eigene Werte ersetzt, sagt es der Absatz
       data-ff-tri-km-neu. Nicht neu belegt wird beim ersten Zeichnen (die
       km aus "Ändern" im Warenkorb bleiben) und beim Wechsel auf eine
       Laufdistanz und zurück auf dieselbe Klasse: kmKlasse merkt sich die
       letzte Klasse auch über einen Lauf hinweg. */
    var kmKlasse = null;
    var kmVorbelegt = null;
    var eTriKmNeu = auswahl(wurzel, 'data-ff-tri-km-neu');

    function kmHinweis(text) {
      if (eTriKmNeu && eTriKmNeu.textContent !== text) eTriKmNeu.textContent = text;
    }

    function klassenName() {
      var option = eDistanz ? eDistanz.options[eDistanz.selectedIndex] : null;
      return option ? option.textContent.trim() : '';
    }

    function kmNormal(text) {
      return String(text || '').replace(/\s+/g, '').replace('.', ',');
    }

    function kmWerte() {
      var aus = [];
      for (var i = 0; i < H2_ARTEN.length; i++) {
        var f = triKm[H2_ARTEN[i]];
        aus.push(f ? kmNormal(f.feld.value) : '');
      }
      return aus;
    }

    function kmSetzen(werte) {
      for (var i = 0; i < H2_ARTEN.length; i++) {
        var f = triKm[H2_ARTEN[i]];
        if (f && f.feld.value !== werte[i]) f.feld.value = werte[i];
      }
    }

    function kmGleich(a, b) {
      return !!a && !!b && a.join('|') === b.join('|');
    }

    function kmVorbelegen(klasse, teile) {
      if (teile) {
        kmSetzen(teile.km);
        kmVorbelegt = teile.km.slice();
        kmKlasse = null;
        kmHinweis('');
        return;
      }
      if (klasse === kmKlasse) return;
      var jetzt = kmWerte();
      var leer = !jetzt[0] && !jetzt[1] && !jetzt[2];
      var unveraendert = kmGleich(jetzt, kmVorbelegt);
      var wechsel = kmKlasse !== null;
      if ((leer || unveraendert || wechsel) && H2_KLASSEN[klasse]) {
        kmSetzen(H2_KLASSEN[klasse]);
        kmVorbelegt = H2_KLASSEN[klasse].slice();
        var ersetzt = wechsel && !leer && !unveraendert && !kmGleich(jetzt, kmVorbelegt);
        var vorlage = eTriKmNeu ? eTriKmNeu.getAttribute('data-ff-text') || '' : '';
        kmHinweis(ersetzt && vorlage ? vorlage.replace('{klasse}', klassenName()) : '');
      }
      kmKlasse = klasse;
    }

    function kmGueltig(text) {
      if (REGELN && REGELN.kmGueltig && REGELN.h2()) return !!REGELN.kmGueltig(text);
      return /^[0-9]{1,3}(?:,[0-9]{1,2})?$/.test(text) && parseFloat(text.replace(',', '.')) > 0;
    }

    /* Die Beschriftung der vier Spalten: aus dem Rennen, sonst aus den
       km (tagesstapel.triathlon_beschriftung), solange eine fehlt nur
       SWIM, BIKE, RUN und FINISH. */
    function h2Beschriftung(km) {
      var teile = rennenTeile();
      if (teile) return teile.spalten;
      var b = REGELN && REGELN.h2Beschriftung ? REGELN.h2Beschriftung(km) : null;
      return b ? b.spalten.concat([b.finish]) : H2_BESCHRIFTUNG_ERSATZ;
    }

    /* Die drei Teilzeiten. Jede ist Pflicht, Form wie die Zielzeit, ihre
       Summe nie über der Gesamtzeit (tagesstapel._teilzeiten_setzen). */
    function teilzeiten() {
      var aus = {};
      for (var i = 0; i < H2_ARTEN.length; i++) {
        var f = teilzeitFelder[H2_ARTEN[i]];
        aus[H2_ARTEN[i]] = f ? zeitAus(f.h, f.m, f.s) : { text: '', fehler: false };
      }
      return aus;
    }

    function sekundenAus(text) {
      if (REGELN && REGELN.sekunden) return REGELN.sekunden(text);
      var teile = /^([0-9]{1,2}):([0-9]{2}):([0-9]{2})$/.exec(text || '');
      return teile ? +teile[1] * 3600 + +teile[2] * 60 + +teile[3] : null;
    }

    /* ---------- Eigenes Foto (assets/ff-foto.js) ----------
       Fehlt das Modul, gibt es die Wahl nicht: das Feld bleibt im Markup
       verborgen, es gilt die Streckenkarte wie bisher. */
    function foto() {
      return window.ffFoto || null;
    }

    function fotoStand() {
      var modul = foto();
      return modul ? modul.stand() : { aktiv: false, bereit: false, bild: '', luecke: null };
    }

    function fotoAktiv() {
      return fotoStand().aktiv;
    }

    /* Ohne Streckenkarte legt der Kunde selbst ein, beim Nachreichen
       steht noch nichts fest: in beiden Fällen gibt es kein Motivfeld. */
    function fotoSichtbarSetzen(nachreichen) {
      var modul = foto();
      if (modul) modul.sichtbar(!nachreichen && !ohneKarte());
    }

    /* ---------- Die Breitenprüfung ----------
       Gemessen wird im canvas, in derselben Schrift und in denselben
       Maßen wie im Bild. Der Grenzwert ist die Innenbreite der
       Finisherzeile: Zuschnitt minus Polster auf beiden Seiten. */
    function messwerkzeug() {
      if (!eCanvas || !eCanvas.getContext) return null;
      /* Ein Canvas-Blocker kann getContext werfen lassen. Dann gilt das
         canvas als nicht vorhanden: die Rechnung mit den Laufweiten der
         Druckschrift springt ein, statt den Kauf dauerhaft zu sperren. */
      try {
        return eCanvas.getContext('2d');
      } catch (e) {
        return null;
      }
    }

    /* Misst das canvas überhaupt (N3)? Ein Browser mit Schutz gegen
       Fingerabdrücke, ein Blocker oder ein fehlendes canvas liefern 0
       oder Unsinn, und dann fiele die Breitenprüfung still aus. Zehn W
       müssen breiter sein als zehn i, und beide breiter als nichts. Das
       Ergebnis ändert sich während der Sitzung nicht. */
    var messungOk = null;
    function messungVerlaesslich() {
      if (messungOk !== null) return messungOk;
      var stift = messwerkzeug();
      messungOk = false;
      if (!stift || !stift.measureText) return messungOk;
      try {
        stift.font = '700 40px sans-serif';
        var breit = stift.measureText('WWWWWWWWWW').width;
        var schmal = stift.measureText('iiiiiiiiii').width;
        messungOk = breit > 0 && schmal > 0 && breit > schmal;
      } catch (fehler) {
        messungOk = false;
      }
      return messungOk;
    }

    /* Der Ersatz ohne canvas und ohne Druckregeln: jedes Zeichen zählt
       so breit wie das breiteste der Schrift, dazu die Laufweite. Das ist
       nie schmaler als der Druck, eher deutlich strenger. */
    var ersatzTabelle = null;
    function ersatzBreite(text, grad, lauf) {
      if (!ersatzTabelle) {
        var quelle = ERSATZ_BREITEN;
        ersatzTabelle = { einheiten: quelle.einheiten, standard: quelle.standard, breit: {} };
        for (var b = 0; b < (quelle.breit || []).length; b++) ersatzTabelle.breit[quelle.breit[b][0]] = quelle.breit[b][1];
      }
      var summe = 0;
      for (var i = 0; i < text.length; ) {
        var punkt = text.codePointAt(i);
        var einheiten = ersatzTabelle.breit[punkt] || ersatzTabelle.standard;
        summe += (einheiten / ersatzTabelle.einheiten) * grad + lauf;
        i += punkt > 0xffff ? 2 : 1;
      }
      return summe;
    }

    /* Schriftfamilie und Gewicht kommen aus dem gerenderten Element,
       nur der Grad aus der Tabelle oben: so misst und zeichnet das
       canvas in derselben Schrift wie die Finisherzeile. Auch ein
       ausgeblendetes Element (display none) hat berechnete Werte. */
    function schrift(element, grad, ersatzGewicht) {
      var familie = 'sans-serif';
      var gewicht = ersatzGewicht || '400';
      if (element && window.getComputedStyle) {
        var stil = window.getComputedStyle(element);
        if (stil.fontFamily) familie = stil.fontFamily;
        if (stil.fontWeight) gewicht = stil.fontWeight;
      }
      return gewicht + ' ' + grad + 'px ' + familie;
    }

    function farbe(element, eigenschaft, ersatz) {
      if (!element || !window.getComputedStyle) return ersatz;
      return window.getComputedStyle(element)[eigenschaft] || ersatz;
    }

    function abstandSetzen(stift, mm) {
      if ('letterSpacing' in stift) stift.letterSpacing = mm * PX + 'px';
    }

    /* Breite in Millimetern, samt Laufweite, wie der Druck sie setzt. */
    function textBreite(text, element, grad, lauf, gewicht) {
      var stift = messwerkzeug();
      if (!stift || !text) return 0;
      stift.font = schrift(element, grad * PX, gewicht);
      abstandSetzen(stift, lauf);
      var breite = stift.measureText(text).width / PX;
      abstandSetzen(stift, 0);
      /* Ohne letterSpacing im canvas kommt die Laufweite rechnerisch dazu. */
      if (!('letterSpacing' in stift)) breite += lauf * text.length;
      return breite;
    }

    /* Der Name steht im Druck in BSD600 (poster.py), die Finisherzeile
       im DOM ebenso (ff-shop.css, ff-sv-finisherzeile-name). */
    function nameZuBreit(name) {
      if (REGELN) return REGELN.befund('name', name) === 'breite';
      var gross = versal(name);
      if (!messungVerlaesslich()) return ersatzBreite(gross, GRAD.name, LAUF.name) > MITTE_MM;
      return textBreite(gross, eFinisherzeileName, GRAD.name, LAUF.name, '600') > MITTE_MM;
    }

    /* Ein Ort, der breiter ist als die freie Mitte, wird in Zeile und
       Bild auf die Mitte gestaucht. Der Schalter setzt außerdem den
       Balken der gezeichneten Startnummer eng. */
    function ortEng(ort) {
      if (REGELN) return !!ort && REGELN.breiteMm('ort', ort) > MITTE_MM;
      return !!ort && messungVerlaesslich() && textBreite(versal(ort), eFinisherzeileOrt, GRAD.ort, LAUF.ort, '700') > MITTE_MM;
    }

    /* Ein selbst geschriebener Lauf, der breiter ist als die freie Mitte,
       ist ein Fehler, keine Stauchung: der Generator staucht nicht, der
       Tagesstapel legt ihn auf die Prüfliste ("Ortszeile zu breit").
       Gestaucht bleibt nur der Ort eines Rennens aus der Liste, den der
       Kunde nicht ändern kann. */
    function ortZuBreit(ort) {
      if (REGELN) return REGELN.befund('ort', ort) === 'breite';
      return ortEng(ort);
    }

    /* Triathlon: Name und Ort gegen die Grenzen der Zeile H2 (h2.grenzen_mm).
       Gestaucht wird nie, auch der Ort eines Rennens aus der Liste nicht:
       der Tagesstapel legt beides auf die Prüfliste. Ohne Datei misst das
       canvas wie bei der bisherigen Zeile. */
    function h2ZuBreit(feld, text) {
      if (!text) return false;
      if (REGELN && REGELN.h2 && REGELN.h2()) return REGELN.h2Befund(feld, text) === 'breite';
      return h2Grad(feld, text) === null;
    }

    /* Der Grad in mm, in dem der Druck Name oder Ort der Zeile H2 setzt:
       die erste Stufe aus H2_STUFEN, mit der das Feld in seine Grenze
       passt (poster.h2_satz). null, wenn auch die kleinste nicht passt;
       mit notfalls dann die kleinste, damit die Zeichnung etwas zeigt. */
    function h2Grad(feld, text, notfalls) {
      var rolle = feld === 'ort' ? 'ort' : 'kopf';
      var stufen = H2_STUFEN[feld];
      var basis = H2_GRAD[rolle];
      var kleinste = (basis * stufen[stufen.length - 1]) / stufen[0];
      if (!text) return basis;
      if (REGELN && REGELN.h2 && REGELN.h2()) {
        var pt = REGELN.h2Stufe(feld, text);
        return pt ? (basis * pt) / stufen[0] : notfalls ? kleinste : null;
      }
      var grenze = feld === 'ort' ? H2.ort_mm : H2.name_mm;
      var element = h2Element(zeileImModulH2, feld);
      var gross = versal(text);
      for (var i = 0; i < stufen.length; i++) {
        var grad = (basis * stufen[i]) / stufen[0];
        var breite = messungVerlaesslich()
          ? textBreite(gross, element, grad, H2_LAUF[rolle], rolle === 'ort' ? '700' : '600')
          : ersatzBreite(gross, grad, H2_LAUF[rolle]);
        if (breite <= grenze) return grad;
      }
      return notfalls ? kleinste : null;
    }

    /* Die Distanz steht in einer Seitenspalte von 60 mm. Breiter liefe
       sie in die Mitte, der Tagesstapel legt sie auf die Prüfliste. */
    function distanzZuBreit(zahl) {
      if (!zahl) return false;
      if (REGELN) return REGELN.befund('distanz', zahl) === 'breite';
      return messungVerlaesslich() && textBreite(zahl, eFinisherzeileDistanz, GRAD.wert, LAUF.wert, '700') > SEITE_MM;
    }

    /* ---------- Das Bild ----------
       Dasselbe Layout wie im DOM, drei Blöcke. Farben und Schriften
       kommen aus der gerenderten Finisherzeile, nie aus dem Code: so
       bleibt Regel 1 heil und Zeichnung und Bild laufen nicht
       auseinander. */
    function bildZeichnen(zeile) {
      var stift = messwerkzeug();
      /* Ohne verlässliche Messung kein Bild: _vorschau bleibt leer, der
         Warenkorb zeigt dann seinen Ersatzzustand (N3). */
      if (!stift || !messungVerlaesslich()) return '';

      var grund = grundfarbe();
      var druck = farbe(eFinisherzeileName, 'fill', 'black');
      var neben = farbe(eFinisherzeileLabel, 'fill', druck);
      var linie = farbe(eFinisherzeileLinie, 'stroke', druck);
      var lage = ZEILEN[zeile.vorlage] || ZEILEN['ohne-zeit'];

      stift.clearRect(0, 0, BREITE, HOEHE);
      stift.fillStyle = grund;
      stift.fillRect(0, 0, BREITE, HOEHE);
      stift.textBaseline = 'alphabetic';
      stift.textAlign = 'center';

      /* Ein Text, mittig auf x, Grundlinie y, alles in Millimetern. */
      function setzen(text, element, grad, lauf, gewicht, fuellung, x, y, maxMm) {
        if (!text) return;
        stift.fillStyle = fuellung;
        stift.font = schrift(element, grad * PX, gewicht);
        abstandSetzen(stift, lauf);
        stift.fillText(text, x * PX, y * PX, maxMm * PX);
      }

      if (zeile.h2) {
        h2BildSetzen(stift, zeile, druck, setzen);
        abstandSetzen(stift, 0);
        return bildAusgeben(stift);
      }

      /* Links: Distanz über KM. Rechts: Zielzeit über TIME, ohne Zeit
         das kurze Datum über DATUM, im selben Grad wie im Druck. */
      setzen(zeile.distanz, eFinisherzeileDistanz, GRAD.wert, LAUF.wert, '700', druck, X_LINKS, SEITE_WERT, SEITE_MM);
      if (zeile.distanz) setzen(zeile.linksLabel, eFinisherzeileLabel, GRAD.label, LAUF.label, '500', neben, X_LINKS, SEITE_LABEL, SEITE_MM);
      setzen(zeile.rechts, eFinisherzeileRechts, GRAD.wert, LAUF.wert, '700', druck, X_RECHTS, SEITE_WERT, SEITE_MM);
      if (zeile.rechts) setzen(zeile.rechtsLabel, eFinisherzeileRechtsLabel, GRAD.label, LAUF.label, '500', neben, X_RECHTS, SEITE_LABEL, SEITE_MM);

      /* Mitte: Ort, Datum ausgeschrieben (nur mit Zeit), Linie, Name. */
      setzen(versal(zeile.ort), eFinisherzeileOrt, GRAD.ort, LAUF.ort, '700', druck, X_MITTE, lage.ort, MITTE_MM);
      if (lage.datum) setzen(zeile.datumLang, eFinisherzeileDatum, GRAD.datum, LAUF.datum, '400', druck, X_MITTE, lage.datum, MITTE_MM);

      stift.strokeStyle = linie;
      stift.lineWidth = 1;
      stift.beginPath();
      stift.moveTo((X_MITTE - LINIE_HALB) * PX, lage.linie * PX);
      stift.lineTo((X_MITTE + LINIE_HALB) * PX, lage.linie * PX);
      stift.stroke();

      setzen(versal(zeile.name), eFinisherzeileName, GRAD.name, LAUF.name, '600', druck, X_MITTE, lage.name, MITTE_MM);
      abstandSetzen(stift, 0);

      return bildAusgeben(stift);
    }

    /* Die Triathlon-Zeile H2 im canvas, Satz wie poster.draw_zeile_h2,
       im selben Bild 1120 x 200 wie die Laufzeile: Name, Ort und Datum
       auf der Kopflinie, je Spalte Balken, Beschriftung und Zeit.
       Schriften aus den gerenderten H2-Elementen der Finisherzeile, die
       Balkenfarben aus druckregeln h2.farben. */
    function h2BildSetzen(stift, zeile, druck, setzen) {
      var h = zeile.h2;
      var el = function (feld) {
        return h2Element(zeileImModulH2, feld);
      };
      /* maxWidth weit über der Grenze: gestaucht wird nicht, eine zu
         breite Angabe hält ohnehin den Knopf an. */
      var frei = H2.breite;
      setzen(versal(zeile.name), el('name'), h.grad.name, H2_LAUF.kopf, '600', druck, H2.name_x, H2.kopf, frei);
      setzen(versal(zeile.ort), el('ort'), h.grad.ort, H2_LAUF.ort, '700', druck, H2.ort_x, H2.kopf, frei);
      setzen(h.datum, el('datum'), H2_GRAD.kopf, H2_LAUF.kopf, '600', druck, H2.datum_x, H2.kopf, frei);
      for (var i = 0; i < 4; i++) {
        var mitte = H2_SPALTEN[i];
        stift.fillStyle = H2_FARBEN ? H2_FARBEN[i] : druck;
        stift.fillRect(
          (mitte - H2.balken_breite / 2) * PX,
          H2.balken_oben * PX,
          H2.balken_breite * PX,
          H2.balken_hoehe * PX
        );
        setzen(h.spalten[i].label, el('label-' + (i + 1)), H2_GRAD.label, H2_LAUF.label, '500', druck, mitte, H2.label, H2.spalte_mm);
        setzen(h.spalten[i].zeit, el('zeit-' + (i + 1)), H2_GRAD.zeit, H2_LAUF.zeit, '700', druck, mitte, H2.zeit, H2.spalte_mm);
      }
    }

    function bildAusgeben(stift) {
      /* Der Wächter (Audit W1, 09.10.2026): ein schwarzes Bild geht nie
         in den Warenkorb. Ohne Grund weicht das Bild nur auf PNG aus,
         das die Durchsichtigkeit behält; passt das PNG nicht in die
         Grenze, bleibt _vorschau leer und der Warenkorb zeigt seinen
         Ersatzzustand (N3). Ein schwarzer Grund wird nicht gespeichert. */
      var ecken = eckenBefund(stift);
      if (ecken === 'schwarz') return '';

      /* PNG zuerst, weil Text darin scharf bleibt. Wird die Eigenschaft
         zu lang, weicht das Bild auf JPEG aus, statt die Bestellung mit
         einem übergroßen Wert zu belasten. Bei 1120 x 200 ist JPEG der
         Normalfall (gemessen am 22.09.2026: PNG rund 24000 Zeichen, JPEG
         rund 12500), die Schrift bleibt bei dieser Größe gut lesbar. */
      try {
        var daten = eCanvas.toDataURL('image/png');
        if (daten.length <= GRENZE) return daten;
        if (ecken === 'durchsichtig') return '';
        daten = eCanvas.toDataURL('image/jpeg', 0.8);
        if (daten.length <= GRENZE) return daten;
        return eCanvas.toDataURL('image/jpeg', 0.6);
      } catch (fehler) {
        return '';
      }
    }

    /* Der Grund des Bildes ist die Grundfarbe der gerenderten
       Finisherzeile (--ff-z-finisherzeile-grund, ein Token). Liefert der
       Browser dafür keine deckende Farbe, kommt das Token selbst aus dem
       Stil, zuletzt --ff-paper-alt aus tokens.css. Kein Farbwert im
       Code: ohne Token bleibt es bei white, wie vor dem 09.10.2026. */
    function grundfarbe() {
      var wert = farbe(eFinisherzeile, 'backgroundColor', '');
      if (deckend(wert)) return wert;
      if (window.getComputedStyle) {
        var kandidaten = [
          eFinisherzeile && window.getComputedStyle(eFinisherzeile).getPropertyValue('--ff-z-finisherzeile-grund'),
          window.getComputedStyle(document.documentElement).getPropertyValue('--ff-paper-alt')
        ];
        for (var i = 0; i < kandidaten.length; i++) {
          var token = String(kandidaten[i] || '').trim();
          if (token && deckend(token)) return token;
        }
      }
      return 'white';
    }

    function deckend(wert) {
      if (!wert || wert === 'transparent') return false;
      var alpha = /^rgba\((?:[^,]*,){3}\s*([\d.]+)\)$/.exec(wert);
      if (alpha && parseFloat(alpha[1]) < 1) return false;
      var schraeg = /\/\s*([\d.]+%?)\s*\)$/.exec(wert);
      if (schraeg && parseFloat(schraeg[1]) < (schraeg[1].indexOf('%') >= 0 ? 100 : 1)) return false;
      return true;
    }

    /* Vier Eckpixel des gezeichneten Bildes: durchsichtig, wenn eine
       Ecke nicht deckend ist, schwarz, wenn eine Ecke deckend und nahezu
       schwarz ist (der Grund ist Creme, Druckfarbe steht nie in einer
       Ecke). Lässt der Browser das Auslesen nicht zu (Schutz gegen
       Fingerabdrücke, verdorbene Fläche), gibt es keinen Befund: dann
       läuft der Export wie bisher. */
    function eckenBefund(stift) {
      var punkte = [[0, 0], [BREITE - 1, 0], [0, HOEHE - 1], [BREITE - 1, HOEHE - 1]];
      try {
        for (var i = 0; i < punkte.length; i++) {
          var p = stift.getImageData(punkte[i][0], punkte[i][1], 1, 1).data;
          if (p[3] < 255) return 'durchsichtig';
          if (p[0] < 32 && p[1] < 32 && p[2] < 32) return 'schwarz';
        }
      } catch (fehler) {
        return '';
      }
      return '';
    }

    /* ---------- Zeichnen und prüfen ----------
       Eine Rechnung, drei Ausgaben: die Finisherzeile, die Fehlertexte und
       der Zustand des Knopfes. */
    var ansageZeit = null;

    /* ---------- Die Rahmenvorschau, Stufe 1 ----------
       Der ganze Rahmen als Zeichnung (snippets/rahmenvorschau.liquid),
       auf der Produktseite zweimal: in der Bühne und, für schmale
       Fenster, im Kaufmodul. Sie liegt zum Teil außerhalb des
       Kaufmoduls, deshalb die Suche im ganzen Dokument.

       Sie ist Anschauung, kein Beleg: in den Warenkorb geht weiter nur
       das Bild der Finisherzeile aus bildZeichnen().

       Beispiel und Eingabe mischen sich nie. Solange nichts eingegeben
       ist, steht der Ausgangszustand aus dem Liquid, mit den
       Beispieldaten des Themes. Ab der ersten Eingabe steht nur noch,
       was der Kunde eingegeben hat, leere Angaben bleiben leer. */
    var rahmen = [];
    (function () {
      var gefunden = document.querySelectorAll('[data-ff-rahmen]');
      for (var r = 0; r < gefunden.length; r++) {
        var svg = gefunden[r];
        var felder = {};
        var ausgang = {};
        var knoten = svg.querySelectorAll('[data-ff-rahmen-feld]');
        for (var k = 0; k < knoten.length; k++) {
          var schluessel = knoten[k].getAttribute('data-ff-rahmen-feld');
          felder[schluessel] = knoten[k];
          ausgang[schluessel] = knoten[k].textContent;
        }
        var bild = svg.querySelector('[data-ff-rahmen-bild]');
        rahmen.push({
          svg: svg,
          h2: h2Sammeln(svg),
          felder: felder,
          ausgang: ausgang,
          bild: bild,
          fotoBild: svg.querySelector('[data-ff-rahmen-foto]'),
          ausgangBild: bild ? bild.getAttribute('href') || '' : '',
          ausgangKarte: svg.getAttribute('data-ff-karte') || 'ohne',
          ausgangLang: felder['rechts-label'] ? felder['rechts-label'].getAttribute('lang') || '' : ''
        });
      }
    })();

    /* Wie viel Platz ein Text in der Zeichnung hat, in ihren Einheiten
       (Millimeter). Das Bild im canvas staucht über maxWidth, die
       Finisherzeile im DOM schneidet ab, das SVG täte ohne dies keins von
       beiden: ein Freitext-Ort mit 60 Zeichen liefe über KM und TIME
       und im Balken über das Blatt hinaus. */
    var RAHMEN_BREITE = { ort: MITTE_MM, name: MITTE_MM, distanz: SEITE_MM, rechts: SEITE_MM, 'nummer-ort': 156 };

    /* Die Finisherzeile der Zeichnung steht im Satz der Druckdatei
       (snippets/finisherzeile-svg.liquid). Dort ist das Datum in der Mitte
       ausgeschrieben, "27. September 2026", wie es der Tagesstapel aus
       dem Renntag bildet (Repo streckenkarte, tagesstapel.py, renntag).
       Ohne Zielzeit rückt der Ort tiefer, rechts steht das kurze Datum. */
    var RAHMEN_MONATE = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    var RAHMEN_ORT_Y = { 'mit-zeit': '518.5', 'ohne-zeit': '521.5' };
    (function () {
      var l = REGELN && REGELN.lage();
      if (!l || !l.poster) return;
      RAHMEN_ORT_Y = {
        'mit-zeit': String(l.poster.fenster_y + l.vorlagen['mit-zeit'].ort),
        'ohne-zeit': String(l.poster.fenster_y + l.vorlagen['ohne-zeit'].ort)
      };
    })();

    function rahmenDatumLang(kurz) {
      var tag = REGELN && REGELN.lauftag(kurz, druckSprache());
      if (tag) return tag[1];
      var teile = /^([0-9]{2})\.([0-9]{2})\.([0-9]{4})$/.exec(kurz || '');
      if (!teile) return kurz || '';
      return parseInt(teile[1], 10) + '. ' + RAHMEN_MONATE[parseInt(teile[2], 10) - 1] + ' ' + teile[3];
    }

    /* Die Finisherzeile schreiben, im Satz der Druckdatei. Ein Weg für
       alle Stellen, an denen sie steht: die Zeile im Kaufmodul und die
       Rahmenzeichnungen. Ort und Name stehen im Druck in Versalien, das
       Datum nicht. */
    function rahmenZeileSchreiben(eintrag, zeile) {
      var ohneZeit = zeile.vorlage === 'ohne-zeit';
      rahmenOrtLage(eintrag, zeile.vorlage);
      rahmenText(eintrag, 'distanz', zeile.distanz);
      rahmenText(eintrag, 'links-label', zeile.distanz ? zeile.linksLabel : '');
      rahmenText(eintrag, 'ort', versal(zeile.ort));
      rahmenText(eintrag, 'datum', ohneZeit ? '' : zeile.datumLang);
      rahmenText(eintrag, 'name', versal(zeile.name));
      rahmenText(eintrag, 'rechts', zeile.rechts);
      rahmenText(eintrag, 'rechts-label', zeile.rechts ? zeile.rechtsLabel : '');

      var label = eintrag.felder['rechts-label'];
      if (label) {
        /* Die Beschriftung steht in der Sprache der Seite, eine eigene
           Auszeichnung braucht sie nicht mehr (bis 29.09.2026 stand TIME
           englisch auf deutschen Seiten). */
        label.removeAttribute('lang');
      }
    }

    /* Die Zeile im Kaufmodul, der Beleg: dieselben Felder wie in der
       Zeichnung, nur ohne Beispiel. */
    var zeileImModul = {
      felder: {
        distanz: eFinisherzeileDistanz,
        'links-label': eFinisherzeileLabel,
        ort: eFinisherzeileOrt,
        datum: eFinisherzeileDatum,
        name: eFinisherzeileName,
        rechts: eFinisherzeileRechts,
        'rechts-label': eFinisherzeileRechtsLabel
      }
    };
    var LINKS_LABEL = eFinisherzeileLabel ? eFinisherzeileLabel.textContent.trim() || 'KM' : 'KM';

    /* ---------- Die Triathlon-Zeile H2 schreiben ----------
       snippets/finisherzeile-h2-svg.liquid steht in der Finisherzeile des
       Kaufmoduls (zweites svg) und in jeder Rahmenvorschau. Ein Weg für
       alle: die Felder über data-ff-h2-feld, die Balkenfarben aus den
       Druckregeln als Stil am Element (kein Token, siehe ff-shop.css). */
    function h2Sammeln(wurzelElement) {
      if (!wurzelElement) return null;
      var felder = {};
      var knoten = wurzelElement.querySelectorAll('[data-ff-h2-feld]');
      for (var k = 0; k < knoten.length; k++) felder[knoten[k].getAttribute('data-ff-h2-feld')] = knoten[k];
      return k ? felder : null;
    }

    function h2Element(felder, feld) {
      return felder ? felder[feld] || null : null;
    }

    function h2Text(felder, feld, text) {
      var knoten = h2Element(felder, feld);
      if (knoten && knoten.textContent !== text) knoten.textContent = text || '';
    }

    /* Der Grad einer Stufe als Stil am Element; die erste Stufe ist der
       Grad aus ff-shop.css, dann bleibt kein Stil stehen. */
    function h2GradSetzen(felder, feld, grad, basis) {
      var knoten = h2Element(felder, feld);
      if (!knoten) return;
      var wert = Math.abs(grad - basis) < 0.0005 ? '' : grad.toFixed(3) + 'px';
      if (knoten.style.fontSize !== wert) knoten.style.fontSize = wert;
    }

    function h2Schreiben(felder, zeile) {
      if (!felder || !zeile.h2) return;
      h2Text(felder, 'name', versal(zeile.name));
      h2Text(felder, 'ort', versal(zeile.ort));
      h2Text(felder, 'datum', zeile.h2.datum);
      h2GradSetzen(felder, 'name', zeile.h2.grad.name, H2_GRAD.kopf);
      h2GradSetzen(felder, 'ort', zeile.h2.grad.ort, H2_GRAD.ort);
      for (var i = 0; i < 4; i++) {
        h2Text(felder, 'label-' + (i + 1), zeile.h2.spalten[i].label);
        h2Text(felder, 'zeit-' + (i + 1), zeile.h2.spalten[i].zeit);
        var balken = h2Element(felder, 'balken-' + (i + 1));
        if (balken && H2_FARBEN) balken.style.fill = H2_FARBEN[i];
      }
    }

    var zeileImModulH2 = h2Sammeln(eFinisherzeile ? eFinisherzeile.querySelector('[data-ff-finisherzeile-h2]') : null);

    /* Die Zeile H2 noch einmal als Text in lesbarer Größe, für das Telefon
       (Prüfstab 2, M3, snippets/finisherzeilenvorschau.liquid). Dieselben
       Werte in derselben Schreibweise wie im Satz darüber. */
    var eH2Lesbar = auswahl(wurzel, 'data-ff-h2-lesbar');

    function h2LesbarSchreiben(zeile) {
      if (!eH2Lesbar) return;
      var an = !!(zeile.h2 && zeile.name);
      eH2Lesbar.hidden = !an;
      if (!an) return;
      var kopf = [];
      if (zeile.name) kopf.push(versal(zeile.name));
      if (zeile.ort) kopf.push(versal(zeile.ort));
      if (zeile.h2.datum) kopf.push(zeile.h2.datum);
      var eKopf = eH2Lesbar.querySelector('[data-ff-h2-lesbar-kopf]');
      var kopfText = kopf.join(MITTELPUNKT);
      if (eKopf && eKopf.textContent !== kopfText) eKopf.textContent = kopfText;
      for (var i = 0; i < 4; i++) {
        var eLabel = eH2Lesbar.querySelector('[data-ff-h2-lesbar-label="' + (i + 1) + '"]');
        var eZeit = eH2Lesbar.querySelector('[data-ff-h2-lesbar-zeit="' + (i + 1) + '"]');
        var spalte = zeile.h2.spalten[i] || { label: '', zeit: '' };
        if (eLabel && eLabel.textContent !== spalte.label) eLabel.textContent = spalte.label || '';
        if (eZeit && eZeit.textContent !== spalte.zeit) eZeit.textContent = spalte.zeit || '';
      }
    }

    function rahmenOrtLage(eintrag, vorlage) {
      var knoten = eintrag.felder.ort;
      if (knoten) knoten.setAttribute('y', RAHMEN_ORT_Y[vorlage] || RAHMEN_ORT_Y['mit-zeit']);
    }

    function rahmenText(eintrag, schluessel, text) {
      var knoten = eintrag.felder[schluessel];
      if (!knoten) return;
      if (knoten.textContent !== text) knoten.textContent = text;

      var breite = RAHMEN_BREITE[schluessel];
      if (!breite || !knoten.getComputedTextLength) return;
      knoten.removeAttribute('textLength');
      knoten.removeAttribute('lengthAdjust');
      /* Die ausgeblendete Fassung misst 0 und bleibt, wie sie ist. */
      if (knoten.getComputedTextLength() > breite) {
        knoten.setAttribute('textLength', breite);
        knoten.setAttribute('lengthAdjust', 'spacingAndGlyphs');
      }
    }

    /* Die Kartengrafik wechselt nie sichtbar von einem Rennen aufs
       andere: bis die neue geladen ist, steht nur die Beschriftung
       (laedt). Lässt sie sich nicht laden, bleibt es dabei (ohne),
       das Fenster ist nie stumm. */
    function rahmenBild(eintrag, adresse, zustand) {
      var bild = eintrag.bild;
      if (!bild || !adresse) return 'ohne';
      if (bild.getAttribute('href') === adresse) {
        return eintrag.bildFehler === adresse ? 'ohne' : eintrag.bildGeladen === adresse ? zustand : 'laedt';
      }
      eintrag.bildZiel = zustand;
      eintrag.bildGeladen = '';
      bild.setAttribute('href', adresse);
      /* Die ausgeblendete Fassung (display none am Vorfahren) lädt in
         manchen Browsern gar nicht und meldete dann nie load: sie
         überspringt den Ladezustand. */
      if (!eintrag.svg.getClientRects().length) {
        eintrag.bildGeladen = adresse;
        return zustand;
      }
      return 'laedt';
    }

    function rahmenBildHorchen(eintrag) {
      if (!eintrag.bild) return;
      /* Das Beispielbild aus dem Liquid kann schon geladen sein, bevor
         dieses Skript läuft: es gilt als geladen, ein späterer Fehler
         nimmt das zurück. */
      eintrag.bildGeladen = eintrag.ausgangBild;
      eintrag.bild.addEventListener('load', function () {
        eintrag.bildGeladen = eintrag.bild.getAttribute('href') || '';
        if (eintrag.svg.getAttribute('data-ff-karte') === 'laedt') {
          eintrag.svg.setAttribute('data-ff-karte', eintrag.bildZiel || 'karte');
        }
      });
      eintrag.bild.addEventListener('error', function () {
        eintrag.bildFehler = eintrag.bild.getAttribute('href') || '';
        var stand = eintrag.svg.getAttribute('data-ff-karte');
        if (stand === 'laedt' || stand === 'karte' || stand === 'beispiel') {
          eintrag.svg.setAttribute('data-ff-karte', 'ohne');
        }
      });
    }

    for (var h = 0; h < rahmen.length; h++) rahmenBildHorchen(rahmen[h]);

    /* Das eigene Foto im Kartenfenster: der Ausschnitt als Object-URL
       (lokal) oder, beim "Ändern", die Vorschau der Position von der
       Hauptdomain. Lädt sie nicht, steht die Beschriftung "Dein Foto". */
    function rahmenFoto(eintrag, adresse) {
      var bild = eintrag.fotoBild;
      if (!bild || !adresse) return 'fotowahl';
      if (bild.getAttribute('href') !== adresse) {
        eintrag.fotoFehler = '';
        bild.setAttribute('href', adresse);
      }
      return eintrag.fotoFehler === adresse ? 'fotowahl' : 'kundenfoto';
    }

    for (var hf = 0; hf < rahmen.length; hf++) {
      (function (eintrag) {
        if (!eintrag.fotoBild) return;
        eintrag.fotoBild.addEventListener('error', function () {
          eintrag.fotoFehler = eintrag.fotoBild.getAttribute('href') || '';
          if (eintrag.svg.getAttribute('data-ff-karte') === 'kundenfoto') eintrag.svg.setAttribute('data-ff-karte', 'fotowahl');
        });
      })(rahmen[hf]);
    }

    /* Welche Karte im Fenster steht, siehe Tabelle im Kopf von
       snippets/rahmenvorschau.liquid. Die Reihenfolge ist die der
       Bestellung (eigenschaftenSchreiben): eine eigene Spur schlägt
       jede Wahl aus der Liste, auch "ohne Karte". */
    function rahmenKarte(nachreichen, quelle) {
      /* Das eigene Foto ersetzt den Inhalt der Kartenfläche, auch neben
         einer eigenen Spur: gedruckt wird dann das Foto (Vertrag v1). */
      var fotoJetzt = nachreichen ? null : fotoStand();
      if (fotoJetzt && fotoJetzt.aktiv) {
        return fotoJetzt.bild ? { zustand: 'kundenfoto', bild: fotoJetzt.bild } : { zustand: 'fotowahl', bild: '' };
      }
      if (nachreichen || !streckenwahl) return { zustand: 'ohne', bild: '' };
      if (quelle === 'gpx' || quelle === 'strava') return { zustand: 'eigen', bild: '' };
      if (ohneKarte()) return { zustand: 'foto', bild: '' };

      /* "Wir melden uns vorher" ist nur in Stufe a zugesagt
         (snippets/streckenwahl.liquid, Hilfetext). In b und c kommt
         die Strecke aus der eigenen Aufzeichnung. */
      var stufe = streckenwahl.getAttribute('data-ff-stufe') || 'a';
      var frei = stufe === 'a' ? 'frei' : 'eigen';

      if (!eRennen) return { zustand: rennenWert() ? frei : 'leer', bild: '' };
      if (eRennen.value === 'frei') return { zustand: frei, bild: '' };
      if (!eRennen.value) return { zustand: 'leer', bild: '' };
      var gewaehlt = eRennen.options[eRennen.selectedIndex];
      var adresse = gewaehlt ? gewaehlt.getAttribute('data-karte') || '' : '';
      return adresse ? { zustand: 'karte', bild: adresse } : { zustand: 'ohne', bild: '' };
    }

    function rahmenZeichnen(zeile, nachreichen) {
      if (!rahmen.length) return;

      /* Eine eigene Strecke (Stufe B und C, ff-strecke.js) hat noch
         keine Kartengrafik: die entsteht erst nach der Bestellung. */
      var quelle = nachreichen ? '' : wurzel.getAttribute('data-ff-streckenquelle') || '';

      /* Unberührt heißt: nichts getippt, nichts gewählt, nichts
         angekreuzt. "Keine Zeit", ein gewähltes "Mein Rennen ist nicht
         dabei" mit noch leerem Freitext und eine übernommene Spur sind
         Eingaben, auch wenn die Zeile davon noch leer ist. */
      var rennenGewaehlt = eRennen ? !!eRennen.value : !!wert(eRennenFreiFeld);
      var keineZeit = !!(eKeineZeit && eKeineZeit.checked);
      var unberuehrt =
        !nachreichen &&
        !zeile.name &&
        !zeile.ort &&
        !zeile.datum &&
        !zeile.distanz &&
        !zeile.zeit &&
        !rennenGewaehlt &&
        !keineZeit &&
        !quelle &&
        !ohneKarte() &&
        !fotoAktiv();
      var karte = rahmenKarte(nachreichen, quelle);

      for (var r = 0; r < rahmen.length; r++) {
        var eintrag = rahmen[r];
        var svg = eintrag.svg;

        if (unberuehrt) {
          for (var schluessel in eintrag.ausgang) {
            if (Object.prototype.hasOwnProperty.call(eintrag.ausgang, schluessel)) {
              rahmenText(eintrag, schluessel, eintrag.ausgang[schluessel]);
            }
          }
          if (eintrag.felder['rechts-label']) {
            if (eintrag.ausgangLang) eintrag.felder['rechts-label'].setAttribute('lang', eintrag.ausgangLang);
            else eintrag.felder['rechts-label'].removeAttribute('lang');
          }
          svg.setAttribute(
            'data-ff-karte',
            eintrag.ausgangKarte === 'beispiel' ? rahmenBild(eintrag, eintrag.ausgangBild, 'beispiel') : eintrag.ausgangKarte
          );
          rahmenOrtLage(eintrag, 'mit-zeit');
          svg.setAttribute('data-ff-beispiel', 'ja');
          svg.setAttribute('data-ff-vorlage', 'mit-zeit');
          svg.setAttribute('data-ff-zeile', 'standard');
          svg.setAttribute('data-ff-ort', 'normal');
          continue;
        }

        /* Erst die Engstellung, dann die Texte: die Breitenmessung in
           rahmenText braucht den Schriftgrad, der danach gilt. */
        svg.setAttribute('data-ff-beispiel', 'nein');
        svg.setAttribute('data-ff-vorlage', zeile.vorlage);
        svg.setAttribute('data-ff-ort', zeile.ortEng ? 'eng' : 'normal');
        /* Triathlon: das Passepartout mit dem H2-Fenster, die bisherige
           Zeile fällt weg (ff-shop.css, data-ff-zeile). */
        svg.setAttribute('data-ff-zeile', zeile.h2 ? 'h2' : 'standard');
        h2Schreiben(eintrag.h2, zeile);

        rahmenZeileSchreiben(eintrag, zeile);
        rahmenText(eintrag, 'nummer-ort', versal(zeile.ort));

        var kartenzustand = karte.zustand;
        if (kartenzustand === 'karte') kartenzustand = rahmenBild(eintrag, karte.bild, 'karte');
        else if (kartenzustand === 'kundenfoto') kartenzustand = rahmenFoto(eintrag, karte.bild);
        svg.setAttribute('data-ff-karte', kartenzustand);
      }
    }

    function zeichnen() {
      var nachreichen = nachreichenAn();

      /* Nachreichen legt die Angabefelder still, blendet sie aber
         nicht aus: sonst springt das Layout (Spec 5.5). Die Strecke
         liegt mit still. Sie ist keins der vier Felder aus Spec 5.5,
         aber die Regel dort endet mit "die Bestellung ist gültig":
         ein Pflichtfeld, das trotzdem stehen bleibt, macht genau das
         unmöglich, und der Geschenkkauf ohne Angaben ist der ganze
         Zweck der Option (Spec 8.6). */
      var stillzulegen = [
        eName,
        eRenntag,
        eDistanz,
        eDistanzFreiFeld,
        eTrailKm,
        eTrailHm,
        eUltraZahl,
        eEinheit,
        eZeitH,
        eZeitM,
        eZeitS,
        eKeineZeit,
        eRennen,
        eRennenFreiFeld,
        eOhneKarte
      ];
      for (var i = 0; i < stillzulegen.length; i++) {
        if (stillzulegen[i]) stillzulegen[i].disabled = nachreichen;
      }

      /* Triathlon: die Zielzeit heißt Gesamtzeit und ist Pflicht, "keine
         Zeit" fällt weg, die Teilzeiten erscheinen. Sonst sind die
         Teilzeiten samt ihren Eigenschaften disabled: so gehen sie nie
         mit der Bestellung, auch nicht leer (der Tagesstapel legt
         Teilzeiten an einem anderen Rahmen auf die Prüfliste). */
      var triathlon = triathlonAn();
      if (eKeineZeitZeile) eKeineZeitZeile.hidden = triathlon;
      if (eKeineZeit && !nachreichen) eKeineZeit.disabled = triathlon;
      if (eZeitOptional) eZeitOptional.hidden = triathlon;
      var zeitArt = triathlon ? 'triathlon' : 'standard';
      for (var zt = 0; zt < 2; zt++) {
        var zeitText = [eZeitName, eZeitHilfe][zt];
        var neuerText = zeitText ? zeitText.getAttribute('data-ff-text-' + zeitArt) : '';
        if (neuerText && zeitText.textContent !== neuerText) zeitText.textContent = neuerText;
      }

      /* Rennen statt Lauf (Prüfstab 2, N1, 27.10.2026): Beschriftungen,
         Hilfen und Platzhalter mit data-ff-sportwort tragen beide
         Fassungen, die Fehler eine zweite mit -triathlon (fehlertext).
         data-ff-sport am Kaufmodul schaltet außerdem das Feldpaar
         Renntag und Distanz einspaltig (ff-produkt.css, M4). */
      sportJetzt = zeitArt;
      if (wurzel.getAttribute('data-ff-sport') !== zeitArt) wurzel.setAttribute('data-ff-sport', zeitArt);
      for (var sw = 0; sw < eSportwoerter.length; sw++) {
        var swText = eSportwoerter[sw].getAttribute('data-ff-text-' + zeitArt);
        if (!swText) continue;
        if (eSportwoerter[sw].getAttribute('data-ff-sportwort') === 'placeholder') {
          if (eSportwoerter[sw].getAttribute('placeholder') !== swText) eSportwoerter[sw].setAttribute('placeholder', swText);
        } else if (eSportwoerter[sw].textContent !== swText) {
          eSportwoerter[sw].textContent = swText;
        }
      }
      if (eTeilzeiten) eTeilzeiten.hidden = !triathlon;

      /* Die km je Disziplin: mit der Klasse vorbelegt, bei einem Rennen
         mit eigenen Teilstrecken fest und gesperrt (readonly, sie gehen
         trotzdem mit). Ohne Triathlon disabled, sie gehen nie mit. */
      var rennenKm = triathlon ? rennenTeile() : null;
      if (triathlon) kmVorbelegen(triathlonKlasse(), rennenKm);
      else kmHinweis('');
      if (eTriathlonBlock) eTriathlonBlock.hidden = !triathlon;
      if (eTriKmHilfe) {
        var kmHilfe = eTriKmHilfe.getAttribute(rennenKm ? 'data-ff-text-fest' : 'data-ff-text-frei');
        if (kmHilfe && eTriKmHilfe.textContent !== kmHilfe) eTriKmHilfe.textContent = kmHilfe;
      }
      for (var tk1 = 0; tk1 < H2_ARTEN.length; tk1++) {
        var kf = triKm[H2_ARTEN[tk1]];
        if (!kf) continue;
        kf.feld.disabled = !triathlon;
        kf.feld.readOnly = !!rennenKm;
        kf.verborgen.disabled = !triathlon;
        if (!triathlon) kf.verborgen.value = '';
      }
      for (var tz = 0; tz < H2_ARTEN.length; tz++) {
        var tf = teilzeitFelder[H2_ARTEN[tz]];
        if (!tf) continue;
        if (tf.h) tf.h.disabled = !triathlon;
        if (tf.m) tf.m.disabled = !triathlon;
        if (tf.s) tf.s.disabled = !triathlon;
        tf.verborgen.disabled = !triathlon;
        if (!triathlon) tf.verborgen.value = '';
      }

      if (eZeitH && !nachreichen) {
        var ohneZeit = !triathlon && !!(eKeineZeit && eKeineZeit.checked);
        eZeitH.disabled = ohneZeit;
        eZeitM.disabled = ohneZeit;
        eZeitS.disabled = ohneZeit;
      }

      if (eDistanzFrei) eDistanzFrei.hidden = !eDistanz || eDistanz.value !== 'frei';
      if (eDistanzTrail) eDistanzTrail.hidden = !eDistanz || eDistanz.value !== 'Trail';
      if (eDistanzUltra) eDistanzUltra.hidden = !eDistanz || eDistanz.value !== 'Ultra';
      if (eEinheitBlock) eEinheitBlock.hidden = !einheitMoeglich();
      if (eHoeheName) {
        var hoeheName = eHoeheName.getAttribute('data-ff-text-' + einheit());
        if (hoeheName && eHoeheName.textContent !== hoeheName) eHoeheName.textContent = hoeheName;
      }
      if (eRennenFrei) {
        /* Ohne Auswahlfeld ist das Freitextfeld der Grundzustand und
           bleibt immer sichtbar, nur der Hilfetext wechselt. */
        if (eRennen) eRennenFrei.hidden = eRennen.value !== 'frei' && !ohneKarte();
        var hilfe = eRennenFrei.querySelector('[data-ff-hilfe-frei]');
        if (hilfe) {
          var attr = ohneKarte() ? 'data-ff-hilfe-ohne-karte' : 'data-ff-hilfe-frei';
          var hilfeText = (triathlon && hilfe.getAttribute(attr + '-triathlon')) || hilfe.getAttribute(attr) || hilfe.textContent;
          if (hilfe.textContent !== hilfeText) hilfe.textContent = hilfeText;
        }
      }

      fotoSichtbarSetzen(nachreichen);

      var name = nachreichen ? '' : druckText(eName);
      var zeit = nachreichen ? { text: '', fehler: false } : zielzeit();
      var satz = distanzSatz();

      /* Die Zeile, einmal gerechnet, dreimal ausgegeben: Finisherzeile,
         Bild und Ansage. Die Vorlage folgt der Zielzeit, der Kunde
         wählt nichts. */
      var zeile = {
        name: name,
        ort: nachreichen ? '' : ortWert(),
        datum: nachreichen ? '' : renntagLesbar(),
        distanz: nachreichen ? '' : satz.zahl,
        zeit: zeit.text,
        vorlage: zeit.text ? 'mit-zeit' : 'ohne-zeit',
        linksLabel: satz.label
      };
      zeile.rechts = zeile.vorlage === 'mit-zeit' ? zeile.zeit : zeile.datum;
      zeile.rechtsLabel = zeile.vorlage === 'mit-zeit' ? label('zeit', 'ZEIT') : label('datum', 'DATUM');
      zeile.ortEng = ortEng(zeile.ort);
      zeile.datumLang = rahmenDatumLang(zeile.datum);

      /* Triathlon: die Zeile H2. Vier Spalten, die ersten drei mit den
         Teilzeiten, FINISH mit der Gesamtzeit (Zielzeit). */
      var teilzeitStand = triathlon ? teilzeiten() : null;
      var kmJetzt = triathlon ? kmWerte() : null;
      if (triathlon) {
        var beschriftung = h2Beschriftung(kmJetzt);
        /* Name und Ort in ihrer Stufe, das Datum in der Sprache der Zeile
           (englisch "5 JUL 2026", Marvin 27.10.2026). */
        zeile.h2 = {
          spalten: [],
          grad: { name: h2Grad('name', zeile.name, true), ort: h2Grad('ort', zeile.ort, true) },
          datum: REGELN && REGELN.h2Datum ? REGELN.h2Datum(zeile.datum, druckSprache()) : zeile.datum
        };
        for (var sp = 0; sp < 4; sp++) {
          zeile.h2.spalten.push({
            label: beschriftung[sp] || '',
            zeit: sp < 3 ? teilzeitStand[H2_ARTEN[sp]].text : zeit.text
          });
        }
      }

      /* Die Zeichnung ist Anschauung. Ein Fehler darin darf weder die
         Finisherzeile noch den Kaufknopf anhalten. */
      try {
        rahmenZeichnen(zeile, nachreichen);
      } catch (fehler) {
        /* bewusst leer */
      }

      if (eFinisherzeile) {
        eFinisherzeile.setAttribute('data-ff-vorlage', zeile.vorlage);
        eFinisherzeile.setAttribute('data-ff-leer', name ? 'nein' : 'ja');
        eFinisherzeile.setAttribute('data-ff-ort', zeile.ortEng ? 'eng' : 'normal');
      }
      /* Erst die Attribute, dann die Texte: die Breitenmessung braucht
         den sichtbaren Satz. */
      rahmenZeileSchreiben(zeileImModul, zeile);
      if (eFinisherzeile) eFinisherzeile.setAttribute('data-ff-zeile', zeile.h2 ? 'h2' : 'standard');
      h2Schreiben(zeileImModulH2, zeile);
      h2LesbarSchreiben(zeile);

      var teile = [];
      if (zeile.ort) teile.push(zeile.ort);
      if (zeile.datum) teile.push(zeile.datum);
      if (!nachreichen && distanzWert()) teile.push(distanzWert());
      if (zeile.zeit) teile.push(zeile.zeit);
      if (zeile.h2) {
        for (var mt = 0; mt < 3; mt++) {
          if (zeile.h2.spalten[mt].zeit) teile.push(zeile.h2.spalten[mt].label + ' ' + zeile.h2.spalten[mt].zeit);
        }
      }
      var meta = teile.join(TRENNER);

      if (eFinisherzeileLeer) {
        eFinisherzeileLeer.hidden = !!name;
        if (nachreichen) {
          eFinisherzeileLeer.hidden = false;
          eFinisherzeileLeer.textContent = textAus(eFinisherzeileLeer, 'nachreichen', 'Die Finisherzeile drucken wir nach, sobald du uns die Angaben schickst');
        }
      }

      /* Die Ansage für Hilfsmittel kommt verzögert, sonst liest der
         Screenreader jeden Tastendruck einzeln vor. */
      if (eAnsage) {
        if (ansageZeit) window.clearTimeout(ansageZeit);
        ansageZeit = window.setTimeout(function () {
          eAnsage.textContent = name
            ? textAus(eAnsage, 'vorlage', 'Deine Finisherzeile: {zeile}').replace('{zeile}', name + (meta ? ', ' + meta : ''))
            : '';
        }, 700);
      }

      /* ---------- Fehlertexte ----------
         Ein Zeichen, das der Druck nicht hat, geht vor: erst wenn alle
         Zeichen druckbar sind, sagt die Breite etwas (M2). */
      var nameZeichen = !nachreichen && name ? fremdesZeichen(name, 'name') : '';
      /* Beim Triathlon gilt die Grenze der Zeile H2 (h2.grenzen_mm.name),
         der Satz ist ein anderer, der Text derselbe. */
      var zuBreit = !nachreichen && !!name && !nameZeichen && (triathlon ? h2ZuBreit('name', name) : nameZuBreit(name));

      if (nameZeichen) {
        fehlerSetzen('name', zeichenFehler('name', nameZeichen), eName);
      } else if (zuBreit) {
        fehlerSetzen('name', fehlertext('name', 'lang'), eName);
      } else if (!nachreichen && !name && (beruehrt.name || abgeschickt)) {
        fehlerSetzen('name', fehlertext('name', 'fehlt'), eName);
      } else {
        fehlerSetzen('name', '', eName);
      }

      /* Beim Triathlon ist die Gesamtzeit Pflicht. Fehlt sie, meldet
         sich das Feld erst nach dem Verlassen oder beim Absenden. */
      var gesamtFehlt = triathlon && !zeit.fehler && !zeit.text;
      if (zeit.fehler) {
        var zeitArt2 = triathlon ? 'gesamt-format' : zeit.art || 'format';
        fehlerSetzen('zeit', fehlertext('zeit', zeitArt2) || fehlertext('zeit', 'format'), eZeitM);
      } else if (gesamtFehlt && (beruehrt.zeit || abgeschickt)) {
        fehlerSetzen('zeit', fehlertext('zeit', 'gesamt-fehlt') || fehlertext('zeit', 'format'), eZeitM);
      } else {
        fehlerSetzen('zeit', '', eZeitM);
      }

      /* Die Teilzeiten: jede Pflicht, jede eine Zeit, zusammen nie über
         der Gesamtzeit (tagesstapel._teilzeiten_setzen). Eine falsche
         Form meldet sich sofort, eine fehlende erst nach dem Verlassen
         oder beim Absenden. Der Text nennt die Disziplin. */
      var teilzeitFehler = false;
      var teilzeitMeldung = '';
      var summe = 0;
      for (var tk = 0; tk < H2_ARTEN.length; tk++) {
        var tfeld = teilzeitFelder[H2_ARTEN[tk]];
        if (!tfeld) continue;
        var stand = triathlon ? teilzeitStand[H2_ARTEN[tk]] : { text: '', fehler: false };
        var falsch = triathlon && (stand.fehler || !stand.text);
        if (falsch) teilzeitFehler = true;
        var zeigen = triathlon && (stand.fehler || (!stand.text && (beruehrt.teilzeiten || abgeschickt)));
        if (zeigen && !teilzeitMeldung) {
          teilzeitMeldung = fehlertext('teilzeiten', stand.fehler ? 'format' : 'fehlt').replace('{disziplin}', tfeld.name);
        }
        var felder3 = [tfeld.h, tfeld.m, tfeld.s];
        for (var f3 = 0; f3 < 3; f3++) {
          if (felder3[f3]) felder3[f3].setAttribute('aria-invalid', zeigen ? 'true' : 'false');
        }
        if (triathlon && stand.text) summe += sekundenAus(stand.text) || 0;
      }
      /* Die Summe zählt erst, wenn alle drei und die Gesamtzeit stehen. */
      var gesamtSekunden = zeit.text ? sekundenAus(zeit.text) : null;
      if (triathlon && !teilzeitFehler && gesamtSekunden !== null && summe > gesamtSekunden) {
        teilzeitFehler = true;
        teilzeitMeldung = fehlertext('teilzeiten', 'summe');
      }
      fehlerSetzen('teilzeiten', teilzeitMeldung, null);

      var distanzFrei = !nachreichen && !!eDistanz && eDistanz.value === 'frei';
      var distanzZeichen = distanzFrei ? fremdesZeichen(distanzWert(), 'distanz') : '';
      var distanzOffen = !nachreichen && !distanzWert();
      var distanzFeld = distanzFrei && eDistanzFreiFeld ? eDistanzFreiFeld : eDistanz;
      if (eDistanz && eDistanz !== distanzFeld) eDistanz.setAttribute('aria-invalid', 'false');
      if (eDistanzFreiFeld && eDistanzFreiFeld !== distanzFeld) eDistanzFreiFeld.setAttribute('aria-invalid', 'false');
      /* Beim Triathlon steht die Distanz nicht in einer Seitenspalte. */
      var distanzBreit = !nachreichen && !triathlon && !distanzZeichen && distanzZuBreit(zeile.distanz);
      if (distanzZeichen) {
        fehlerSetzen('distanz', zeichenFehler('distanz', distanzZeichen), distanzFeld);
      } else if (distanzBreit) {
        fehlerSetzen('distanz', fehlertext('distanz', 'lang'), distanzFeld);
      } else {
        fehlerSetzen(
          'distanz',
          distanzOffen && (beruehrt.distanz || abgeschickt) ? fehlertext('distanz', 'fehlt') : '',
          distanzFeld
        );
      }

      /* Freie Distanz und Ultra: eine Strecke, die keine Zahl ist, hält
         den Knopf an, wie bei Trail. */
      var freiFehler = distanzFrei && !!eDistanzFreiFeld && freiAngaben().zahl.fehler;
      var ultraFehler = !nachreichen && ultraAn() && ultraAngaben().zahl.fehler;
      if (eUltraZahl) eUltraZahl.setAttribute('aria-invalid', ultraFehler ? 'true' : 'false');
      if (freiFehler && !distanzZeichen) {
        fehlerSetzen('distanz', fehlertext('distanz', 'zahl') || fehlertext('distanz', 'trail-km'), eDistanzFreiFeld);
      }
      /* Triathlon: jede Strecke eine Zahl in km, größer 0. Leer meldet sich
         erst nach dem Verlassen. Eine eigene Spur geht beim Triathlon
         nicht, das sagt derselbe Kasten. */
      var kmFehler = false;
      var kmMeldung = '';
      var kmFeldFehler = null;
      for (var tk2 = 0; tk2 < H2_ARTEN.length; tk2++) {
        var kg = triKm[H2_ARTEN[tk2]];
        if (!kg) continue;
        var kmWert = triathlon ? kmWerte()[tk2] : '';
        var kmFalsch = triathlon && (!kmWert || !kmGueltig(kmWert));
        if (kmFalsch) kmFehler = true;
        var kmZeigen = kmFalsch && (!!kmWert || beruehrt.distanz || abgeschickt);
        kg.feld.setAttribute('aria-invalid', kmZeigen ? 'true' : 'false');
        if (kmZeigen && !kmMeldung) {
          kmMeldung = fehlertext('distanz', 'tri-km').replace('{disziplin}', kg.name);
          kmFeldFehler = kg.feld;
        }
      }
      var spurFehler = triathlonSpur();
      if (spurFehler) {
        fehlerSetzen('distanz', fehlertext('distanz', 'tri-spur'), eDistanz);
      } else if (kmMeldung) {
        fehlerSetzen('distanz', kmMeldung, kmFeldFehler);
      }

      if (ultraFehler) {
        fehlerSetzen('distanz', fehlertext('distanz', 'zahl') || fehlertext('distanz', 'trail-km'), eUltraZahl);
      }

      /* Trail: eine Zahl, die keine ist, hält den Knopf an. Die Meldung
         steht im selben Kasten wie die übrigen Distanzfehler. */
      var trail = !nachreichen && trailAn() ? trailAngaben() : null;
      var trailFehler = !!trail && (trail.km.fehler || trail.hm.fehler);
      if (eTrailKm) eTrailKm.setAttribute('aria-invalid', trail && trail.km.fehler ? 'true' : 'false');
      if (eTrailHm) eTrailHm.setAttribute('aria-invalid', trail && trail.hm.fehler ? 'true' : 'false');
      if (trailFehler) {
        var trailArt = trail.km.fehler ? 'trail-km' : einheit() === 'mi' ? 'trail-ft' : 'trail-hm';
        fehlerSetzen('distanz', fehlertext('distanz', trailArt), trail.km.fehler ? eTrailKm : eTrailHm);
      }

      /* Fehlt der Tag, meldet sich das Feld erst nach dem Verlassen.
         Ein Tag in der Zukunft oder vor 1970 sofort. */
      var renntagArt = nachreichen ? '' : renntagFehler();
      var renntagOffen = !!renntagArt;
      var renntagZeigen = renntagArt && (renntagArt !== 'fehlt' || beruehrt.renntag || abgeschickt);
      fehlerSetzen('renntag', renntagZeigen ? fehlertext('renntag', renntagArt) || fehlertext('renntag', 'fehlt') : '', eRenntag);

      /* Die Ortszeile kommt nur beim Freitext vom Kunden. Beim Rennen
         aus der Liste steht der Ort am Eintrag im Store. */
      var ortZeichen = !nachreichen && !!streckenwahl && freitextFall() ? fremdesZeichen(rennenWert(), 'ort') : '';
      var ortBreit = !nachreichen && !!streckenwahl && freitextFall() && !ortZeichen && ortZuBreit(ortWert());
      var rennenOffen = !nachreichen && !!streckenwahl && !rennenWert();
      /* aria-invalid steht an dem Feld, das der Kunde ändern muss, das
         andere wird zurückgesetzt. */
      var rennenFeld = eRennen && !freitextFall() ? eRennen : eRennenFreiFeld || eRennen;
      if (eRennen && eRennen !== rennenFeld) eRennen.setAttribute('aria-invalid', 'false');
      if (eRennenFreiFeld && eRennenFreiFeld !== rennenFeld) eRennenFreiFeld.setAttribute('aria-invalid', 'false');
      /* Triathlon: der Ort eines Rennens aus der Liste wird in der Zeile H2
         nicht gestaucht. Passt er nicht in h2.grenzen_mm.ort, legte der
         Tagesstapel die Position auf die Prüfliste: dann lieber vorher
         sagen und anhalten. Das trifft einen Eintrag im Store, nicht den
         Kunden, der Text bittet deshalb um eine Nachricht. */
      var ortBreitH2 = triathlon && !!ortWert() && h2ZuBreit('ort', ortWert());
      var rennenKonflikt = triathlonRennenKonflikt();
      if (ortZeichen) {
        fehlerSetzen('rennen', zeichenFehler('rennen', ortZeichen), rennenFeld);
      } else if (rennenKonflikt) {
        fehlerSetzen('rennen', fehlertext('rennen', 'triathlon'), rennenFeld);
      } else if (ortBreitH2) {
        /* Beim Freitext kann der Kunde kürzen, beim Rennen aus der Liste nicht. */
        var ortArt = freitextFall() ? 'lang' : 'lang-h2';
        fehlerSetzen('rennen', fehlertext('rennen', ortArt) || fehlertext('rennen', 'lang'), rennenFeld);
      } else if (ortBreit) {
        fehlerSetzen('rennen', fehlertext('rennen', 'lang'), rennenFeld);
      } else {
        fehlerSetzen(
          'rennen',
          rennenOffen && (beruehrt.rennen || abgeschickt) ? fehlertext('rennen', 'fehlt') : '',
          rennenFeld
        );
      }

      /* ---------- Der Knopf ---------- */
      var vollstaendig =
        !zuBreit &&
        !nameZeichen &&
        !distanzZeichen &&
        !distanzBreit &&
        !ortZeichen &&
        !ortBreit &&
        !ortBreitH2 &&
        !rennenKonflikt &&
        !kmFehler &&
        !spurFehler &&
        !zeit.fehler &&
        !gesamtFehlt &&
        !teilzeitFehler &&
        !trailFehler &&
        !freiFehler &&
        !ultraFehler &&
        !distanzOffen &&
        !renntagOffen &&
        !rennenOffen;
      if (!nachreichen && !name) vollstaendig = false;

      /* Das eigene Foto: gewählt, gelesen, groß genug, Bildrechte bestätigt. */
      var fotoJetzt = fotoStand();
      if (fotoJetzt.aktiv && !fotoJetzt.bereit) vollstaendig = false;
      if (foto()) foto().pflichtZeigen(abgeschickt);

      var hakenNoetig = !nachreichen;
      var hakenGesetzt = !hakenNoetig || !!(eHaken && eHaken.checked);

      if (eHaken) {
        var hakenZeile = eHaken.closest ? eHaken.closest('.ff-haken') : null;
        if (hakenZeile) hakenZeile.hidden = !hakenNoetig;
      }

      letzterStand = { vollstaendig: vollstaendig, haken: hakenGesetzt, grund: vollstaendig ? '' : knopfGrund() };
      knopfSetzen();

      return { name: name, meta: meta, zeit: zeit.text, zeile: zeile, vollstaendig: vollstaendig, haken: hakenGesetzt };
    }

    /* Der Knopf folgt dem letzten Stand, der Sperre beim Absenden und
       dem Nachladen der Position. */
    function knopfSetzen() {
      if (!eKnopf) return;
      var vollstaendig = letzterStand.vollstaendig;
      var hakenGesetzt = letzterStand.haken;
      var frei = vollstaendig && hakenGesetzt && !sendet && !vorbelegungLaeuft;
      eKnopf.setAttribute('aria-disabled', frei ? 'false' : 'true');

      if (!eKnopfhinweis) return;
      if (sendet && hochladeText) {
        eKnopfhinweis.textContent = hochladeText;
      } else if (sendet) {
        eKnopfhinweis.textContent = position !== null
          ? textAus(eModul, 'sendet-aendern', 'Deine Angaben werden geändert')
          : textAus(eModul, 'sendet', 'Deine Angaben gehen in den Warenkorb');
      } else if (vorbelegungLaeuft) {
        eKnopfhinweis.textContent = textAus(eModul, 'laedt', 'Deine Angaben werden geladen');
      } else if (!vollstaendig) {
        eKnopfhinweis.textContent = letzterStand.grund || textAus(eModul, 'unvollstaendig', 'Oben fehlt noch eine Angabe');
      } else if (!hakenGesetzt) {
        eKnopfhinweis.textContent = eKnopfhinweis.getAttribute('data-ff-haken-text') || 'Setz das Häkchen, dann geht es weiter';
      } else {
        eKnopfhinweis.textContent = textAus(eModul, 'bereit', 'Eine Position, deine Angaben gehen mit');
      }
    }

    /* ---------- Warum der Knopf zu ist (Prüfstab 2, N5, 27.10.2026) ----------
       Bis dahin stand unter dem gesperrten Knopf immer "Oben fehlt noch
       eine Angabe", auch wenn nichts fehlte, sondern der Ort zu lang oder
       das Foto zu klein war (Prüfstab 5, B1). Jetzt nennt der Hinweis den
       Bereich der ersten Lücke (data-ff-bereich am Feldblock) und, wenn
       dort eine Meldung steht, diese Meldung: "Rennen: Das passt nicht
       auf die Triathlon-Zeile ..." oder "Oben fehlt noch: Name". Ein
       Klick führt wie bisher zur Lücke. Ohne Bereich bleibt der alte Satz. */
    function knopfGrund() {
      var luecke = ersteLuecke();
      var block = luecke && luecke !== eHaken && luecke.closest ? luecke.closest('[data-ff-bereich]') : null;
      if (!block) {
        /* Eine Meldung ohne markiertes Feld, etwa die Summe der Teilzeiten. */
        var offen = form.querySelectorAll('[data-ff-fehler][data-an="ja"]');
        for (var i = 0; i < offen.length && !block; i++) {
          block = offen[i].closest ? offen[i].closest('[data-ff-bereich]') : null;
        }
      }
      if (!block) return '';
      var bereich = block.getAttribute('data-ff-bereich') || '';
      var meldung = '';
      var kaesten = block.querySelectorAll('[data-ff-fehler][data-an="ja"], [data-ff-foto-guete][data-stufe="gesperrt"]');
      for (var k = 0; k < kaesten.length && !meldung; k++) {
        if (kaesten[k].closest && kaesten[k].closest('[hidden]')) continue;
        meldung = (kaesten[k].textContent || '').trim();
      }
      if (meldung) {
        return textAus(eModul, 'fehler', '{bereich}: {fehler}').replace('{bereich}', bereich).replace('{fehler}', meldung);
      }
      return textAus(eModul, 'luecke', 'Oben fehlt noch: {bereich}').replace('{bereich}', bereich);
    }

    /* "Das Zeichen ‹x› können wir nicht drucken." Der Satz steht in
       locales/de.default.json und kommt über das Fehlerfeld herein. */
    function zeichenFehler(name, zeichen) {
      var vorlage = fehlertext(name, 'zeichen') || 'Das Zeichen ‹{zeichen}› können wir nicht drucken.';
      return vorlage.replace('{zeichen}', zeichenZeigen(zeichen));
    }

    /* ---------- Die Eigenschaften an der Position ----------
       Genau die Schlüssel aus Spec 4.3. Leere Werte bleiben leer:
       Shopify zeigt sie dann nicht als leere Zeile im Warenkorb. */
    function eigenschaftenSchreiben(stand) {
      var nachreichen = nachreichenAn();

      propSetzen('Name', nachreichen ? '' : stand.name);
      propSetzen('Lauftag', nachreichen ? '' : renntagLesbar());
      propSetzen('Distanz', nachreichen ? '' : distanzWert());
      propSetzen('Zielzeit', nachreichen ? '' : stand.zeit);
      /* Die Teilzeiten nur beim Triathlon. Ihre Felder sind sonst
         disabled und gehen gar nicht mit (zeichnen). */
      var h2Zeile = !nachreichen && stand.zeile ? stand.zeile.h2 : null;
      var kmBestellung = h2Zeile ? kmWerte() : null;
      for (var tk3 = 0; tk3 < H2_ARTEN.length; tk3++) {
        var kv = triKm[H2_ARTEN[tk3]];
        if (kv) kv.verborgen.value = kmBestellung ? kmBestellung[tk3] : '';
      }
      for (var ts = 0; ts < H2_ARTEN.length; ts++) {
        var tv = teilzeitFelder[H2_ARTEN[ts]];
        if (tv) tv.verborgen.value = h2Zeile ? h2Zeile.spalten[ts].zeit : '';
      }
      propSetzen('_nachreichen', nachreichen ? 'ja' : '');
      /* Die Sprache gilt auch beim Nachreichen: wer verschenkt, weiß,
         in welcher Sprache der Beschenkte die Zeile lesen will. */
      if (eSprachen.length) propSetzen('Sprache', spracheWahl());

      if (streckenwahl) {
        propSetzen('Lauf', nachreichen ? '' : rennenWert());
        /* Stufe B und C: ff-strecke.js legt Quelle (gpx, strava) und
           Referenz (die Polyline) als Attribute am Kaufmodul ab. Liegt
           eine eigene Strecke vor, schlägt sie die Listenwahl. */
        var eigeneQuelle = wurzel.getAttribute('data-ff-streckenquelle') || '';
        var eigeneReferenz = wurzel.getAttribute('data-ff-streckenreferenz') || '';
        if (eigeneQuelle && eigeneReferenz) {
          propSetzen('_streckenquelle', nachreichen ? '' : eigeneQuelle);
          propSetzen('_streckenreferenz', nachreichen ? '' : eigeneReferenz);
        } else {
          var quelle = 'auswahl';
          if (ohneKarte()) quelle = 'ohne-karte';
          else if (rennenAusFreitext()) quelle = 'freitext';
          propSetzen('_streckenquelle', nachreichen ? '' : quelle);
          propSetzen('_streckenreferenz', nachreichen ? '' : streckenreferenz());
        }
      }

      /* Die Prüfmarkierung hält die Bestellung aus der automatischen
         Produktion: eine andere Distanz und ein selbst geschriebenes
         Rennen bekommen beide einen menschlichen Blick (Spec 5.3, 7.1).
         Nachgereicht wird ebenfalls markiert: eine Bestellung ohne
         Angaben kann gar nicht automatisch in den Druck, sie wartet
         auf die Nachmeldung. */
      var markieren = false;
      if (nachreichen) markieren = true;
      if (eDistanz && eDistanz.value === 'frei') markieren = true;
      if (streckenwahl && !nachreichen && rennenAusFreitext()) markieren = true;
      /* Eine eigene Spur (GPX, Strava) sieht immer ein Mensch vor dem Druck (Spec 7.2, Punkt 3). */
      if (streckenwahl && !nachreichen && wurzel.getAttribute('data-ff-streckenquelle')) markieren = true;
      /* Jedes eigene Foto sieht ein Mensch vor dem Druck (Vertrag v1). Die
         fünf Eigenschaften des Fotos schreibt ff-foto.js, bei der
         Streckenkarte leert es sie. */
      if (!nachreichen && fotoAktiv()) markieren = true;
      if (foto()) foto().eigenschaftenSchreiben();
      propSetzen('_pruefmarkierung', markieren ? 'ja' : '');
    }

    /* Die erste Stelle, an der der Kunde etwas tun muss, in der
       Reihenfolge, in der das Formular gelesen wird. Ein optionales,
       leeres Feld (die Zielzeit) ist keine Lücke, ein ausgeblendetes
       Freitextfeld auch nicht. */
    function ersteLuecke() {
      var reihe = [
        { feld: eRennen, pflicht: true },
        { feld: eRennenFreiFeld, pflicht: !!streckenwahl && freitextFall() },
        /* Das Motiv steht im Formular direkt nach der Streckenwahl. */
        { foto: true },
        { feld: eName, pflicht: true },
        { feld: eRenntag, pflicht: true },
        { feld: eDistanz, pflicht: true },
        { feld: eDistanzFreiFeld, pflicht: !!eDistanz && eDistanz.value === 'frei' },
        { feld: eTrailKm, pflicht: false },
        { feld: eUltraZahl, pflicht: false },
        { feld: eTrailHm, pflicht: false },
        { feld: eZeitH, pflicht: false },
        { feld: eZeitM, pflicht: triathlonAn() && !wert(eZeitH) && !wert(eZeitS) }
      ];
      /* Triathlon: Gesamtzeit und Teilzeiten sind Pflicht. Ein leeres
         Feld einer Zeit ist keine Lücke, solange ein anderes der drei
         gefüllt ist (45 Minuten ohne Stunde sind gültig). */
      for (var tkl = 0; tkl < H2_ARTEN.length; tkl++) {
        var kl = triKm[H2_ARTEN[tkl]];
        if (kl) reihe.splice(6 + tkl, 0, { feld: kl.feld, pflicht: triathlonAn() });
      }
      for (var tl = 0; tl < H2_ARTEN.length; tl++) {
        var tlf = teilzeitFelder[H2_ARTEN[tl]];
        if (!tlf) continue;
        reihe.push({ feld: tlf.h, pflicht: false });
        reihe.push({ feld: tlf.m, pflicht: triathlonAn() && !wert(tlf.h) && !wert(tlf.s) });
        reihe.push({ feld: tlf.s, pflicht: false });
      }
      for (var i = 0; i < reihe.length; i++) {
        if (reihe[i].foto) {
          var fotoLuecke = fotoStand().luecke;
          if (fotoLuecke) return fotoLuecke;
          continue;
        }
        var feld = reihe[i].feld;
        if (!feld || feld.disabled || (feld.closest && feld.closest('[hidden]'))) continue;
        if (feld.getAttribute('aria-invalid') === 'true') return feld === eZeitM ? eZeitH || feld : feld;
        if (reihe[i].pflicht && !wert(feld)) return feld === eZeitM ? eZeitH || feld : feld;
      }
      if (eHaken && !eHaken.disabled && !eHaken.checked && !(eHaken.closest && eHaken.closest('[hidden]'))) return eHaken;
      return null;
    }

    function merken(daten) {
      try {
        /* Ohne Bild wird auch das alte vergessen: die Danke-Seite zeigte
           sonst die Zeile einer früheren Bestellung. */
        if (!daten) window.sessionStorage.removeItem('ff_letzte_vorschau');
        else window.sessionStorage.setItem('ff_letzte_vorschau', daten);
      } catch (fehler) {
        /* Ohne Speicher zeigt die Danke-Seite die Zeile nicht noch
           einmal. Das ist ein Verlust an Freude, kein Fehler im Kauf. */
      }
    }

    /* Das eigene Foto merkt sich das Kaufmodul bewusst nicht für die
       Danke-Seite (Review 17.10.2026). Ein Merkplatz je Tab kann nicht
       wissen, zu welcher Bestellung das Foto gehört: ein späteres
       Absenden mit Karte löschte ihn, ein gescheitertes Hinzufügen nach
       gelungenem Hochladen hängte das Foto an die nächste Bestellung. Die
       Danke-Seite zeigt das Foto nur aus dem Bestellobjekt. */

    /* ---------- Die Positionskennung (A-413) ----------
       Bei jedem Absenden neu und zufällig. Sie trägt keine Bedeutung,
       sie macht nur zwei sonst gleiche Positionen verschieden, damit
       Shopify sie nicht zu einer mit Menge 2 zusammenfasst. */
    function positionsKennung() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return String(Date.now()) + '-' + Math.random().toString(36).slice(2);
    }

    /* ---------- Absenden ----------
       Drei Sicherungen aus dem Code-Review vom 25.09.2026:

       M1  Nach dem ersten gültigen Absenden ist der Knopf gesperrt
           (Merker sendet, aria-disabled, danach disabled), bis die
           Antwort da ist. Ein Doppelklick legte sonst zwei Positionen
           an, jede mit eigener Positionskennung. Ein Fehler gibt ihn
           wieder frei, die Rückkehr über "Zurück" auch (pageshow).
       M4  Wirft irgendetwas in diesem Handler, geht das Formular nicht
           ab: sonst sendete der Browser die Form mit leeren
           Eigenschaften, eine Bestellung ohne Namen. Statt dessen steht
           ein Fehlertext unter dem Knopf.
       N4  _vorschau_geprueft wird bei jedem Absenden neu gesetzt, beim
           Nachreichen leer. Nach "Zurück" stand sonst der Zeitstempel
           der ersten Bestätigung an der zweiten Bestellung. */
    function sperren(an) {
      sendet = an;
      if (eKnopf) {
        if (an) {
          eKnopf.setAttribute('aria-busy', 'true');
          /* disabled erst nach dem Absenden: gesetzt im Handler selbst,
             hielte manch älterer Browser die laufende Absendung an. Der
             Merker sperrt schon vorher. */
          window.setTimeout(function () {
            if (sendet) eKnopf.disabled = true;
          }, 0);
        } else {
          eKnopf.disabled = false;
          eKnopf.removeAttribute('aria-busy');
        }
      }
      knopfSetzen();
    }

    /* Eine Meldung unter dem Knopf an- oder ausschalten. Die sichtbare
       hängt an aria-describedby des Knopfs, eine verborgene nicht: ein
       direkt verwiesenes Element läse das Vorlesegerät auch verborgen. */
    function meldung(name, an) {
      var kasten = wurzel.querySelector('[data-ff-fehler="' + name + '"]');
      if (!kasten) return;
      kasten.setAttribute('data-an', an ? 'ja' : 'nein');
      if (!eKnopf || !kasten.id) return;
      var liste = (eKnopf.getAttribute('aria-describedby') || '').split(/\s+/);
      var neu = [];
      for (var i = 0; i < liste.length; i++) if (liste[i] && liste[i] !== kasten.id) neu.push(liste[i]);
      if (an) neu.push(kasten.id);
      eKnopf.setAttribute('aria-describedby', neu.join(' '));
    }

    form.addEventListener('submit', function (ereignis) {
      if (sendet || vorbelegungLaeuft) {
        ereignis.preventDefault();
        return;
      }

      try {
        abgeschickt = true;
        meldung('skript', false);
        meldung('technisch', false);
        meldung('foto-upload', false);
        var stand = zeichnen();
        angabenPruefen();

        if (!stand.vollstaendig || !stand.haken) {
          ereignis.preventDefault();
          var luecke = ersteLuecke();
          /* Liegt die Lücke in der Kurzfassung, erst aufklappen: ein
             verborgenes Feld nimmt keinen Fokus an. */
          if (luecke && luecke.closest && luecke.closest('[data-ff-angaben]')) angabenAuf();
          if (luecke && luecke.focus) luecke.focus();
          return;
        }

        eigenschaftenSchreiben(stand);
        propSetzen('_position', positionsKennung());

        var bild = bildZeichnen(stand.zeile);
        propSetzen('_vorschau', bild);
        merken(bild);

        propSetzen('_vorschau_geprueft', nachreichenAn() ? '' : new Date().toISOString());

        /* Das eigene Foto geht zuerst hoch, erst danach die Position in
           den Warenkorb. Scheitert es, bleibt alles stehen, der Kunde
           liest unter dem Knopf, was zu tun ist. form.submit() löst
           dieses Ereignis nicht noch einmal aus. */
        if (fotoAktiv()) {
          ereignis.preventDefault();
          fotoAbsenden();
          return;
        }

        /* Kam der Kunde über "Ändern" aus dem Warenkorb, wird die
           bestehende Position geändert und keine zweite angelegt
           (Spec 12.1, Warenkorb 3). */
        if (position !== null && window.fetch) {
          ereignis.preventDefault();
          sperren(true);
          positionAendern();
          return;
        }

        sperren(true);
      } catch (fehler) {
        ereignis.preventDefault();
        sperren(false);
        meldung('skript', true);
      }
    });

    function fotoFehlerZeigen(meldungstext) {
      var kasten = wurzel.querySelector('[data-ff-fehler="foto-upload"]');
      if (kasten) kasten.textContent = meldungstext || '';
      meldung('foto-upload', !!meldungstext);
    }

    function fotoAbsenden() {
      var modul = foto();
      fotoFehlerZeigen('');
      hochladeText = modul.hochladeText();
      sperren(true);

      modul
        .hochladen()
        .then(function (ergebnis) {
          if (!ergebnis) throw new Error('ohne Ergebnis');
          modul.eigenschaftenSchreiben();
          hochladeText = '';
          if (position !== null && window.fetch) {
            positionAendern();
            return;
          }
          knopfSetzen();
          form.submit();
        })
        .catch(function (fehler) {
          hochladeText = '';
          sperren(false);
          fotoFehlerZeigen(fehler && fehler.meldung ? fehler.meldung : textAus(eModul, 'foto-fehler', 'Dein Foto ließ sich gerade nicht senden. Versuch es gleich noch einmal.'));
          zeichnen();
        });
    }

    /* Zurück aus dem Warenkorb: die Seite kommt aus dem Zwischenspeicher
       des Browsers, samt gesperrtem Knopf und den versteckten Werten der
       letzten Absendung. Beides wird zurückgesetzt, die Eingaben bleiben. */
    window.addEventListener('pageshow', function (ereignis) {
      if (!ereignis.persisted) return;
      propSetzen('_vorschau_geprueft', '');
      propSetzen('_vorschau', '');
      propSetzen('_position', '');
      hochladeText = '';
      sperren(false);
      zeichnen();
    });

    function eigenschaftenSammeln() {
      var gesammelt = {};
      var felder = wurzel.querySelectorAll('[data-ff-prop]');

      for (var i = 0; i < felder.length; i++) {
        /* Was disabled ist, geht nicht mit, wie beim Absenden des
           Formulars: die Teilzeiten an einem Rahmen ohne Triathlon. */
        if (felder[i].disabled) continue;
        var schluessel = felder[i].getAttribute('data-ff-prop');
        gesammelt[schluessel] = felder[i].value || '';
      }

      return gesammelt;
    }

    function korbJsonAdresse() {
      return wurzel.getAttribute('data-korb-json') || '/cart.js';
    }

    function korbLesen() {
      return window
        .fetch(korbJsonAdresse(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (antwort) {
          return antwort.ok ? antwort.json() : null;
        });
    }

    function positionImKorb(korb, schluessel) {
      var liste = (korb && korb.items) || [];
      for (var i = 0; i < liste.length; i++) {
        if (liste[i].key === schluessel) return liste[i];
      }
      return null;
    }

    /* Die Position gibt es nicht mehr (in einem anderen Tab entfernt
       oder schon geändert, dann trägt sie einen neuen Schlüssel). Kein
       stilles Anlegen einer zweiten: der Kunde liest, was passiert, und
       der Knopf heißt wieder "In den Warenkorb". */
    function positionVerloren() {
      position = null;
      if (eKnopftext) eKnopftext.textContent = KNOPFTEXT;
      meldung('position', true);
    }

    function positionAendern() {
      var ziel = wurzel.getAttribute('data-aendern') || '/cart/change.js';
      var korb = wurzel.getAttribute('data-warenkorb') || '/cart';

      window
        .fetch(ziel, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: position, quantity: 1, properties: eigenschaftenSammeln() })
        })
        .then(function (antwort) {
          if (antwort.ok) {
            window.location.href = korb;
            return null;
          }
          /* Abgelehnt: liegt die Position noch im Korb? Wenn nicht, ist
             das kein technischer Fehler, sondern ein anderer Korb. */
          return korbLesen().then(function (inhalt) {
            if (inhalt && !positionImKorb(inhalt, position)) {
              positionVerloren();
              sperren(false);
              zeichnen();
              return null;
            }
            throw new Error('Antwort ' + antwort.status);
          });
        })
        .catch(function () {
          /* Deine Angaben stehen noch da, versuch es gleich noch
             einmal. Der Text steht im Markup, nicht hier (Spec 5.6). */
          meldung('technisch', true);
          sperren(false);
          zeichnen();
        });
    }

    /* ---------- Vorbelegung aus der Adresse ---------- */
    function vorbelegen() {
      var such;
      try {
        such = new URLSearchParams(window.location.search);
      } catch (fehler) {
        return;
      }

      var name = such.get('name');
      if (name && eName) eName.value = name;

      var renntag = such.get('renntag');
      if (renntag && eRenntag) {
        if (/^[0-9]{2}\.[0-9]{2}\.[0-9]{4}$/.test(renntag)) {
          var teile = renntag.split('.');
          eRenntag.value = teile[2] + '-' + teile[1] + '-' + teile[0];
        } else if (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(renntag)) {
          eRenntag.value = renntag;
        }
      }

      var distanz = such.get('distanz');
      var trailWerte = trailZerlegen(distanz);
      var ultraWerte = ultraZerlegen(distanz);
      if (distanz && eDistanz) {
        if (auswahlHat(eDistanz, distanz) && distanz !== 'frei') {
          eDistanz.value = distanz;
        } else if (ultraWerte && eUltraZahl && auswahlHat(eDistanz, 'Ultra')) {
          eDistanz.value = 'Ultra';
          eUltraZahl.value = ultraWerte.zahl;
          if (eEinheit) eEinheit.value = ultraWerte.einheit;
        } else if (trailWerte && eTrailKm && eTrailHm && auswahlHat(eDistanz, 'Trail')) {
          /* "30 km" allein kann Trail oder eine freie Distanz sein, beides
             druckt gleich. Wie bis zum 30.09.2026 gewinnt Trail. */
          eDistanz.value = 'Trail';
          eTrailKm.value = trailWerte.km;
          eTrailHm.value = trailWerte.hm;
          if (eEinheit && trailWerte.einheit) eEinheit.value = trailWerte.einheit;
        } else if (auswahlHat(eDistanz, 'frei')) {
          eDistanz.value = 'frei';
          var frei = trailZerlegen(distanz);
          if (eDistanzFreiFeld) eDistanzFreiFeld.value = frei && frei.km && !frei.hm ? frei.km : distanz;
          if (eEinheit && frei && frei.einheit) eEinheit.value = frei.einheit;
        }
      }

      var zeit = such.get('zeit');
      if (zeit && eZeitH) {
        var stuecke = zeit.split(':');
        eZeitH.value = stuecke[0] || '';
        eZeitM.value = stuecke[1] || '';
        eZeitS.value = stuecke[2] || '';
      }

      /* Die km je Disziplin beim Triathlon (warenkorb: km_swim, km_bike,
         km_run). Stehen sie da, belegt die Klasse sie nicht mehr vor. */
      for (var kv2 = 0; kv2 < H2_ARTEN.length; kv2++) {
        var kmAdresse = such.get('km_' + H2_ARTEN[kv2]);
        if (kmAdresse && triKm[H2_ARTEN[kv2]]) triKm[H2_ARTEN[kv2]].feld.value = kmAdresse;
      }

      /* Die Teilzeiten beim Triathlon (warenkorb: zeit_swim, zeit_bike,
         zeit_run). Ob die Felder sichtbar werden, entscheidet die Distanz. */
      for (var tv = 0; tv < H2_ARTEN.length; tv++) {
        var teilwert = such.get('zeit_' + H2_ARTEN[tv]);
        var tfv = teilzeitFelder[H2_ARTEN[tv]];
        if (!teilwert || !tfv || !tfv.h) continue;
        var tstuecke = teilwert.split(':');
        tfv.h.value = tstuecke[0] || '';
        tfv.m.value = tstuecke[1] || '';
        tfv.s.value = tstuecke[2] || '';
      }

      if (such.get('nachreichen') === 'ja' && eNachreichen) eNachreichen.checked = true;

      /* Die Sprache der Position, wie sie im Warenkorb steht. Ein Wert,
         den es als Option nicht gibt, lässt die Vorauswahl stehen. */
      var sprache = such.get('sprache');
      for (var s = 0; sprache && s < eSprachen.length; s++) {
        if (eSprachen[s].value === sprache) eSprachen[s].checked = true;
      }

      streckeVorbelegen(such.get('rennen') || '', such.get('quelle') || '', such.get('referenz') || '');

      /* Ein Rennen aus der Adresse (Story-Sticker, Shop-Grid, Kopf-Link)
         belegt Distanz und Renntag vor wie eine Wahl im Auswahlfeld.
         Nicht beim "Ändern" aus dem Warenkorb: dort kommen beide aus der
         Position. Eine Distanz oder ein Renntag aus der Adresse bleibt
         auch so stehen, gefüllt wird nur ein leeres Feld. */
      var schluessel = such.get('key');
      if (!schluessel) ausRennenVorbelegen();

      /* Eine Position mit eigenem Foto: die Wahl steht sofort, Foto und
         Ausschnitt kommen aus /cart.js (positionNachladen). */
      var motivFoto = such.get('motiv') === 'foto' && !!foto();
      if (motivFoto) foto().motivSetzen('foto');

      if (schluessel &&/^[0-9]+:[A-Za-z0-9]+$/.test(schluessel)) {
        position = schluessel;
        if (eKnopftext) eKnopftext.textContent = textAus(eModul, 'knopf-aendern', 'Angaben übernehmen');
        positionNachladen(such.get('quelle') || '', motivFoto);
      }
    }

    /* Die Streckenwahl so, wie sie an der Position steht (H2). Vorher
       kam nur der Rennname zurück, und aus einem Listenrennen, einer
       Bestellung ohne Karte oder einer eigenen Spur wurde beim Ändern
       stillschweigend "Mein Rennen ist nicht dabei". */
    function streckeVorbelegen(rennen, quelle, referenz) {
      /* Seit dem 27.10.2026 genügt die Referenz allein (Prüfstab 6, Stufe S,
         Punkt 5): ein Link aus /events/ kann rennen:<slug>-<jahr> aus Slug
         und Renntag ableiten und braucht die Bezeichnung nicht mehr. */
      if (!rennen && !quelle && !referenz) return;

      if (quelle === 'ohne-karte') {
        if (eRennen && auswahlHat(eRennen, 'ohne-karte')) eRennen.value = 'ohne-karte';
        else if (eOhneKarte) eOhneKarte.checked = true;
        if (eRennenFreiFeld && rennen) eRennenFreiFeld.value = rennen;
        return;
      }

      if (eRennen && /^rennen:/.test(referenz)) {
        for (var i = 0; i < eRennen.options.length; i++) {
          if (eRennen.options[i].getAttribute('data-referenz') === referenz) {
            eRennen.value = eRennen.options[i].value;
            return;
          }
        }
      }

      /* Ein Listenrennen ohne Referenz (ältere Adresse) oder mit einer
         eigenen Spur: der Rennname ist dann die Bezeichnung aus der Liste. */
      if (eRennen && rennen && rennen !== 'frei' && rennen !== 'ohne-karte' && auswahlHat(eRennen, rennen)) {
        eRennen.value = rennen;
        return;
      }

      /* Freitext, oder das Rennen steht nicht mehr in der Liste. */
      if (eRennenFreiFeld && rennen) {
        if (eRennen) eRennen.value = 'frei';
        eRennenFreiFeld.value = rennen;
      }
    }

    /* Prüft, ob die Position noch im Korb liegt, und holt eine eigene
       Spur (gpx:, strava:) aus ihr: die Polyline ist ein bis zwei
       Kilobyte lang und gehört nicht in die Adresse. Bis die Antwort da
       ist, bleibt der Knopf zu. Kommt eine eigene Spur nicht zurück,
       bleibt er zu: ohne sie überschriebe das Absenden die Strecke an
       der Position mit "freitext". */
    function positionNachladen(quelle, motivFoto) {
      if (!window.fetch) return;
      var eigeneSpur = quelle === 'gpx' || quelle === 'strava';
      vorbelegungLaeuft = true;
      knopfSetzen();

      korbLesen()
        .then(function (korb) {
          if (!korb) return false;
          var eintrag = positionImKorb(korb, position);
          if (!eintrag) {
            positionVerloren();
            return true;
          }
          var eigenschaften = eintrag.properties || {};
          /* Das eigene Foto der Position bleibt, mit seinem Ausschnitt.
             Kommt es nicht zurück, bleibt die Wahl Foto ohne Bild stehen:
             der Knopf geht erst auf, wenn der Kunde ein Foto wählt oder
             zur Streckenkarte wechselt. Nichts wird still überschrieben. */
          if (motivFoto && foto()) foto().behalten(eigenschaften);
          if (!eigeneSpur) return true;
          var q = eigenschaften._streckenquelle || '';
          var referenz = eigenschaften._streckenreferenz || '';
          if ((q !== 'gpx' && q !== 'strava') || referenz.indexOf(q + ':') !== 0) return false;
          wurzel.setAttribute('data-ff-streckenquelle', q);
          wurzel.setAttribute('data-ff-streckenreferenz', referenz);
          /* ff-strecke.js zeichnet daraus die Linie, wenn es geladen ist. */
          if (typeof window.CustomEvent === 'function') {
            wurzel.dispatchEvent(new window.CustomEvent('ff-strecke-vorbelegt'));
          }
          return true;
        })
        .catch(function () {
          return false;
        })
        .then(function (fertig) {
          if (!fertig && eigeneSpur) {
            meldung('skript', true);
            return;
          }
          vorbelegungLaeuft = false;
          zeichnen();
        });
    }

    function auswahlHat(feld, gesucht) {
      for (var i = 0; i < feld.options.length; i++) {
        if (feld.options[i].value === gesucht) return true;
      }
      return false;
    }

    /* ---------- Vorbelegung aus dem Rennen ----------
       Ein Rennen aus der Liste trägt seine Distanz (data-distanz, Feld
       distanz des Metaobjekts rennen) und seinen Renntag (data-renntag,
       Feld renntag, JJJJ-MM-TT). Die Distanz geht ins Distanzfeld, denn
       der Tagesstapel im Repo streckenkarte prüft die Distanz der
       Bestellung gegen events/<slug>.yaml. Eindeutig ist sie immer:
       dieselbe Route mit zwei Distanzen sind zwei Einträge
       (snippets/streckenwahl.liquid, Regel 3).

       Drei Grenzen, für beide Felder gleich:
       - Ausgelöst wird sie durch eine Wahl des Kunden im Auswahlfeld
         (change) und durch ein Rennen aus der Adresse (vorbelegen(),
         seit 02.10.2026: vorher blieben Distanz und Renntag bei jedem
         Link mit rennen und referenz leer). Nicht beim "Ändern" aus dem
         Warenkorb (key in der Adresse): das behält Distanz und Renntag
         der Position.
       - Überschrieben wird nur ein leeres Feld oder eines, das noch die
         letzte Vorbelegung trägt. Hat der Kunde den Wert danach selbst
         gesetzt, bleibt seiner, auch beim nächsten Rennen.
       - "Mein Rennen ist nicht dabei" und "Ohne Streckenkarte" belegen
         nichts vor. Ein stehender Wert bleibt, nur der Hinweis, er
         komme aus dem Rennen, fällt weg. Dasselbe gilt für einen
         Eintrag ohne Renntag im Store. */
    var eDistanzVorbelegt = auswahl(wurzel, 'data-ff-distanz-vorbelegt');
    var distanzVorbelegt = null;

    function distanzHinweis(text) {
      if (eDistanzVorbelegt && eDistanzVorbelegt.textContent !== text) eDistanzVorbelegt.textContent = text;
    }

    var eRenntagVorbelegt = auswahl(wurzel, 'data-ff-renntag-vorbelegt');
    var renntagVorbelegt = null;

    function renntagHinweis(text) {
      if (eRenntagVorbelegt && eRenntagVorbelegt.textContent !== text) eRenntagVorbelegt.textContent = text;
    }

    function ausRennenVorbelegen() {
      if (!eRennen || nachreichenAn()) return;
      var gewaehlt = eRennen.options[eRennen.selectedIndex];
      var referenz = gewaehlt ? gewaehlt.getAttribute('data-referenz') || '' : '';
      var ausListe = referenz.indexOf('rennen:') === 0;
      if (eDistanz) distanzVorbelegen(ausListe ? gewaehlt.getAttribute('data-distanz') || '' : '');
      if (eRenntag) renntagVorbelegen(ausListe ? gewaehlt.getAttribute('data-renntag') || '' : '');
    }

    function distanzVorbelegen(distanz) {
      /* Ein Triathlon aus der Liste trägt am Metaobjekt nur "Triathlon".
         Vorbelegt wird die Klasse aus H2_BESCHRIFTUNG, sonst Langdistanz. */
      if (distanz === TRIATHLON) {
        var teileListe = rennenTeile();
        distanz = TRIATHLON + ' ' + ((teileListe && teileListe.klasse) || 'Langdistanz');
      }
      if (!distanz || distanz === 'frei' || !auswahlHat(eDistanz, distanz)) {
        distanzHinweis('');
        return;
      }

      var ueberschreibbar = !eDistanz.value || (distanzVorbelegt !== null && eDistanz.value === distanzVorbelegt);
      if (!ueberschreibbar) return;

      eDistanz.value = distanz;
      distanzVorbelegt = distanz;
      var vorlage = eDistanzVorbelegt ? eDistanzVorbelegt.getAttribute('data-ff-text') || '' : '';
      distanzHinweis((vorlage || 'Aus deinem Rennen übernommen: {distanz}. Du kannst sie ändern.').replace('{distanz}', distanz));
    }

    /* Ein Renntag aus dem Store, der die Prüfung in renntagFehler() nicht
       bestünde (kein echtes Datum, vor 1970, zu weit voraus), wird nicht
       gesetzt: der Kunde soll keinen Fehler sehen, den er nicht gemacht
       hat. */
    function renntagVorbelegen(renntag) {
      if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(renntag) || !echtesDatum(renntag) || renntag < RENNTAG_AB || renntag > renntagBis()) {
        renntagHinweis('');
        return;
      }

      var ueberschreibbar = !eRenntag.value || (renntagVorbelegt !== null && eRenntag.value === renntagVorbelegt);
      if (!ueberschreibbar) return;

      eRenntag.value = renntag;
      renntagVorbelegt = renntag;
      var teile = renntag.split('-');
      var lesbar = teile[2] + '.' + teile[1] + '.' + teile[0];
      var vorlage = eRenntagVorbelegt ? eRenntagVorbelegt.getAttribute('data-ff-text') || '' : '';
      renntagHinweis((vorlage || 'Aus deinem Rennen übernommen: {datum}. Du kannst ihn ändern.').replace('{datum}', lesbar));
    }

    /* Das Auswahlfeld selbst hört vor dem Formular: das change erreicht
       erst danach das Formular, und zeichnen() sieht schon die neue
       Distanz und den neuen Renntag. Finisherzeile und Rahmen zeigen sie
       sofort. */
    if (eRennen) eRennen.addEventListener('change', ausRennenVorbelegen);

    /* Wählt der Kunde die Distanz selbst, gilt sie als seine. */
    if (eDistanz) {
      eDistanz.addEventListener('change', function () {
        distanzVorbelegt = null;
        distanzHinweis('');
      });
    }

    /* Dasselbe für den Renntag. Das Datumsfeld meldet jede Eingabe als
       input, nicht erst beim Verlassen: schon ein getippter Tag macht ihn
       zu seinem. Ein Setzen aus dem Skript löst keines der beiden aus. */
    if (eRenntag) {
      var renntagEigen = function () {
        if (renntagVorbelegt !== null && eRenntag.value === renntagVorbelegt) return;
        renntagVorbelegt = null;
        renntagHinweis('');
      };
      eRenntag.addEventListener('input', renntagEigen);
      eRenntag.addEventListener('change', renntagEigen);
    }

    /* ---------- Kurzfassung der übernommenen Angaben ----------
       21.10.2026, Conversion nach Klick, Hebel 2 (kaufmodul.liquid). Sind
       Lauftag und Distanz beide aus dem gewählten Lauf übernommen,
       klappen Lauftag, Distanz und Sprache zu einer Zeile mit "Ändern"
       zusammen. Verborgen wird über data-zu und CSS nur unter 960 px,
       nie über hidden: ersteLuecke() überspringt Felder in [hidden], und
       ein verborgenes Pflichtfeld muss beim Absenden gefunden werden.

       Offen bleibt es, sobald der Kunde "Ändern" drückt, einen der Werte
       selbst setzt oder ein Feld darin einen Fehler zeigt. Ein neuer
       Lauf aus der Liste klappt nur zu, solange der Kunde nicht
       aufgeklappt hat. */
    var eAngabenKurz = auswahl(wurzel, 'data-ff-angaben-kurz');
    var eAngabenText = auswahl(wurzel, 'data-ff-angaben-text');
    var eAngabenAendern = auswahl(wurzel, 'data-ff-angaben-aendern');
    var eAngaben = wurzel.querySelectorAll('[data-ff-angaben]');
    var angabenAufgeklappt = false;

    function angabenZu(zu) {
      for (var i = 0; i < eAngaben.length; i++) eAngaben[i].setAttribute('data-zu', zu ? 'ja' : 'nein');
      if (eAngabenKurz) eAngabenKurz.setAttribute('data-an', zu ? 'ja' : 'nein');
      if (eAngabenAendern) eAngabenAendern.setAttribute('aria-expanded', zu ? 'false' : 'true');
    }

    function angabenText() {
      var teile = eRenntag.value.split('-');
      var datum = teile.length === 3 ? teile[2] + '.' + teile[1] + '.' + teile[0] : eRenntag.value;
      var option = eDistanz.options[eDistanz.selectedIndex];
      var distanz = option ? option.textContent.trim() : eDistanz.value;
      var sprache = '';
      for (var i = 0; i < eSprachen.length; i++) {
        if (!eSprachen[i].checked) continue;
        var marke = eSprachen[i].parentNode ? eSprachen[i].parentNode.querySelector('span') : null;
        sprache = marke ? marke.textContent.trim() : eSprachen[i].value;
      }
      var vorlage =
        (triathlonAn() && eAngabenText.getAttribute('data-ff-text-triathlon')) ||
        eAngabenText.getAttribute('data-ff-text') ||
        'Lauftag {datum} · {distanz} · Finisherzeile auf {sprache}.';
      return vorlage.replace('{datum}', datum).replace('{distanz}', distanz).replace('{sprache}', sprache);
    }

    function angabenFehler() {
      for (var i = 0; i < eAngaben.length; i++) {
        if (eAngaben[i].querySelector('[data-ff-fehler][data-an="ja"]')) return true;
      }
      return false;
    }

    function angabenPruefen() {
      if (!eAngabenKurz || !eAngabenText || !eAngaben.length || !eRenntag || !eDistanz) return;
      /* "Anderer Lauf" und "Ohne Streckenkarte" lassen stehende Werte
         und die Merker stehen (distanzVorbelegen mit leerem Wert),
         deshalb zählt zusätzlich, ob gerade ein Lauf aus der Liste
         gewählt ist. */
      var gewaehlt = eRennen ? eRennen.options[eRennen.selectedIndex] : null;
      var ausListe = !!gewaehlt && (gewaehlt.getAttribute('data-referenz') || '').indexOf('rennen:') === 0;
      var uebernommen =
        ausListe &&
        !nachreichenAn() &&
        renntagVorbelegt !== null &&
        distanzVorbelegt !== null &&
        eRenntag.value === renntagVorbelegt &&
        eDistanz.value === distanzVorbelegt;
      var zu = uebernommen && !angabenAufgeklappt && !angabenFehler();
      if (zu) {
        var text = angabenText();
        if (eAngabenText.textContent !== text) eAngabenText.textContent = text;
      }
      angabenZu(zu);
    }

    function angabenAuf() {
      angabenAufgeklappt = true;
      angabenZu(false);
    }

    if (eAngabenAendern) {
      eAngabenAendern.addEventListener('click', function () {
        angabenAuf();
        if (eRenntag && eRenntag.focus) eRenntag.focus();
      });
    }

    /* ---------- Anhören ---------- */
    form.addEventListener('input', function (ereignis) {
      /* Eigene km nach dem Klassenwechsel: der Hinweis hat seinen Dienst getan. */
      var ziel = ereignis && ereignis.target;
      if (ziel && ziel.hasAttribute && ziel.hasAttribute('data-ff-tri-km')) kmHinweis('');
      zeichnen();
      angabenPruefen();
    });

    /* Minuten und Sekunden zweistellig, sobald der Kunde das Feld verlässt
       (Prüfstab 2, N7, 27.10.2026): aus "5" wird "05", wie es nach "Ändern"
       aus dem Warenkorb ohnehin zurückkommt. Die Stunde bleibt, wie sie ist. */
    function zweistelligAuffuellen(feld) {
      if (!feld || !feld.hasAttribute) return;
      var minuteSekunde =
        feld === eZeitM || feld === eZeitS || feld.hasAttribute('data-ff-teilzeit-m') || feld.hasAttribute('data-ff-teilzeit-s');
      if (minuteSekunde && /^[0-9]$/.test(feld.value)) feld.value = '0' + feld.value;
    }

    /* ff-foto.js meldet jede Änderung am Foto: gelesen, verschoben,
       gezoomt, Bildrechte. */
    wurzel.addEventListener('ff-foto-geaendert', function () {
      zeichnen();
    });

    form.addEventListener('change', function () {
      zeichnen();
      angabenPruefen();
    });

    form.addEventListener(
      'blur',
      function (ereignis) {
        var ziel = ereignis.target;
        if (!ziel) return;
        zweistelligAuffuellen(ziel);
        if (ziel === eName) beruehrt.name = true;
        if (ziel === eRenntag) beruehrt.renntag = true;
        if (ziel === eDistanz || ziel === eDistanzFreiFeld || ziel === eTrailKm || ziel === eTrailHm || ziel === eUltraZahl || ziel === eEinheit) {
          beruehrt.distanz = true;
        }
        if (ziel === eRennen || ziel === eRennenFreiFeld) beruehrt.rennen = true;
        if (ziel === eZeitH || ziel === eZeitM || ziel === eZeitS) beruehrt.zeit = true;
        if (ziel.hasAttribute && ziel.hasAttribute('data-ff-tri-km')) beruehrt.distanz = true;
        if (ziel.hasAttribute && (ziel.hasAttribute('data-ff-teilzeit-h') || ziel.hasAttribute('data-ff-teilzeit-m') || ziel.hasAttribute('data-ff-teilzeit-s'))) {
          beruehrt.teilzeiten = true;
        }
        zeichnen();
      },
      true
    );

    /* Der Kalender bietet keinen Tag nach der Vorlaufgrenze an. Die
       Prüfung in renntagFehler() gilt trotzdem, getippt geht immer. */
    if (eRenntag) eRenntag.max = renntagBis();

    /* Was von einer früheren Absendung in den versteckten Feldern steht
       (der Browser stellt Formularwerte beim Zurückgehen wieder her),
       gilt nicht für diese. */
    propSetzen('_vorschau_geprueft', '');
    propSetzen('_vorschau', '');
    propSetzen('_position', '');

    vorbelegen();
    zeichnen();
    angabenPruefen();

    /* Erst jetzt, da alles hängt, geht der Knopf auf (M4). */
    if (eKnopf) eKnopf.disabled = false;

    /* Die Breitenprüfung braucht die geladene Displayschrift. Vorher
       misst sie den Ersatz und wäre zu großzügig. */
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () {
        zeichnen();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
